// Local, file-based octree LOD streaming — Phase 2 of the streaming plan
// (see /Users/uldis.liepins/.claude/plans/witty-watching-star.md).
//
// Parses a lod-meta.json (as produced by `splat-transform ... lod-meta.json`)
// and, once per frame (throttled to real camera movement), decides which
// leaf node should be resident at which LOD level, bounded by a fixed
// splat budget — then turns that decision into alloc/write/free calls
// against a SplatPool and a streaming sort worker. Decoding a chunk file is
// dispatched to a small worker pool (sog-chunk-worker.js); this phase talks
// to files over plain fetch (via each worker's own UrlReadFileSystem), with
// no ref-counting/cooldown eviction of whole files yet — decoded files are
// cached for the session (see DecodePool._cache) since a file often backs
// several leaf nodes at different [offset,count) slices. Real multi-file
// network transport with eviction is a later phase.
//
// This module owns the *decision + residency* half of streaming; SplatPool
// owns GPU storage, sog-chunk-worker.js owns decode+conversion.

import * as THREE from 'three';
import { nextBucket } from './splatPool';
import { loadTrace } from '../utils/loadTrace';
import { getAccessQuery } from '../utils/lodChunkCache';

// Distance thresholds are geometric: thresholds[level] = baseDistance *
// LOD_MULTIPLIER^level. Level 0 (finest) is only used very close to the
// camera; each higher (coarser) level covers a wider distance band.
// baseDistance is derived per-scene from the AVERAGE LEAF (chunk) RADIUS
// (see SplatOctree's constructor), scaled by this multiple — not from the
// whole scene's bounding box. See that constructor's comment for why:
// scene-extent-based versions (tried twice — a fixed 6-world-unit constant,
// then 0.08 * scene-maxDim) both measured the wrong quantity, one too small
// for a real scene's scale outright, the other still tighter than a single
// chunk's own radius. LOD_MULTIPLIER's geometric growth rate is
// scale-invariant on its own and doesn't need this treatment.
// Multiplied by the average chunk radius (see SplatOctree's constructor)
// to get baseDistance. Deliberately generous: at scale=1 (no budget
// pressure) we want a normal framing to land at level 0-1 across most of
// what's on screen, and let the BUDGET SOLVER be the thing that coarsens
// to fit — _desiredLevel can only ever coarsen relative to its scale=1
// answer, never refine past it, so if scale=1 already picks coarse levels
// for a wide shot, extra budget can't buy back the detail. A small base
// (tried 0.7 ≈ PlayCanvas's literal 5) made a whole-room view sit at
// level 2-3 by distance alone even with 9M of budget unused.
const CHUNK_RADIUS_MULTIPLE = 5.0;
// Between this codebase's earlier 2.2 and PlayCanvas's default of 3.
const LOD_MULTIPLIER = 2.6;
// Two-stage eviction for chunks that have left the frustum:
//  - after CULL_DEMOTE_MS: drop to the COARSEST level (if not already there).
//    The coarsest level of the whole scene is a tiny fraction of the budget
//    (all leaves combined ~= one fine chunk), so keeping every chunk that's
//    ever been on screen resident at coarse costs almost nothing — and it
//    means panning back to an area shows coarse detail INSTANTLY instead of
//    a black hole that then re-streams from scratch (the "pan, big region
//    goes black for ~1s, repopulates chunk by chunk, camera hitches"
//    symptom). The next solve refines it back up normally.
//  - after CULL_FREE_MS: free it entirely (even the coarse copy) — a chunk
//    gone this long is genuinely out of the session's working set.
// The budget solver still coarsens *visible* demand under real pressure; a
// held coarse copy is counted against the budget at its (small) real size.
const CULL_DEMOTE_MS = 2500;
const CULL_FREE_MS = 8000;
// …but only while the pool is actually getting full. Below this share of
// the pool in use, off-screen chunks are kept exactly as they are: turning
// around in one spot used to demote then free every chunk behind the camera
// on the timers above and re-stream it on the way back — hundreds of small
// GPU texture uploads per second, which on Apple GPUs stall the frame
// (measured on an M4 iPad: fps halving circle after circle). The budget
// solver plans visible chunks only, so held ones never starve the view;
// an allocation that doesn't fit still evicts the oldest (_evictOffscreen).
const OFFSCREEN_HOLD_UNTIL = 0.8;
// Finished decodes applied to the GPU per frame. Each is a synchronous
// texture upload; batching all of a tick's finished decodes into one frame
// is the frame spike behind the periodic "smear" flicker. Keep this a touch
// above the per-solve request cap below so the queue drains toward empty
// during continuous motion instead of growing without bound.
// Wall-clock budget per frame for applying finished decodes (each is a
// synchronous GPU texture upload). A time box stays smooth across device
// speeds where a fixed chunk count is either too slow or too spiky. Bumped
// during a tuning-slider boost, when the user is deliberately waiting on
// the scene to re-converge and a brief frame-rate dip is the right trade.
const WRITE_BUDGET_MS = 3.5;
const WRITE_BUDGET_MS_BOOST = 7;
// While the loading overlay covers the scene (setLoading) — a long frame
// can't be seen there, and uploads were the slow tail of a first load.
const WRITE_BUDGET_MS_LOADING = 20;
// viewStatus(): share of the bar for the first pass; the at-rest refinement
// fills the rest (it adds many chunks but few splats).
const LOAD_FIRST_PASS_SHARE = 0.92;
// How far ahead (seconds of current camera velocity) to prefetch. Leaves
// near the projected point but not yet on screen get their coarsest level
// requested at low priority.
const PREFETCH_SECONDS = 0.6;
// Prefetch sort-key bias: prefetch candidates are ordered after every real
// on-screen candidate so they only consume the per-solve request budget
// once visible work is satisfied.
const PREFETCH_DIST_BIAS = 1e9;
// Max decoded chunk files kept in memory. Files backing a resident leaf are
// exempt (compaction needs them); this only bounds the "recently used but no
// longer needed" tail. ~20-50 MB per entry, so keep it modest. Lower on a
// low-memory device (see the `lowMemory` constructor option below) — at 14
// entries this alone can be 280-700 MB, on top of the pool/sort-worker's own
// footprint, which is how streamed-SOG (meant to use LESS memory than a
// static load by only keeping a bounded working set resident) ended up
// crashing an iPhone 13 mini / 16 anyway: nothing here scaled with device
// the way the static path's autoTierFor/estimateAvailableMemoryBytes
// already do.
const MAX_DECODE_CACHE = 14;
const MAX_DECODE_CACHE_LOW_MEMORY = 4;
// New decode requests issued per solve, ALWAYS nearest-leaf-first (see the
// candidate sort in _applyDesiredLevels). High enough that a fresh view
// fills in quickly; the queue ceiling below is the real backstop.
const MAX_REQUESTS_PER_SOLVE = 12;
// Hard ceiling on in-flight decodes + queued writes. Without it a scene
// with a very fine octree (a city split into thousands of leaves) requests
// a level for every visible leaf at once. Requests past this are deferred
// to a later solve (pendingLevel stays -1); since candidates are sorted by
// distance, the nearest leaves are always the ones that get through.
// Lower on a low-memory device: EVERY file backing a currently-queued-but-
// not-yet-applied write is exempt from the decode-cache eviction below
// (_applyDesiredLevels' `keep` set), so a large queue can hold that many
// distinct decoded files resident regardless of MAX_DECODE_CACHE — this was
// the real ceiling on the initial catch-up burst's peak memory, not the
// cache size cap alone.
const MAX_WRITE_QUEUE = 240; // was 80 — coverage of many small leaves needs more in flight (most are decode-cache hits)
const MAX_WRITE_QUEUE_LOW_MEMORY = 16;
// The catch-up boost (_boosting(), the first ~4s of a load) deliberately
// relaxes the per-solve request cap from MAX_REQUESTS_PER_SOLVE to fill the
// view fast — see _boostUntil's own comment. On a low-memory device that
// same burst is the actual crash trigger (confirmed live: iPhone 13 mini
// progressed to ~99% loaded, i.e. right through this burst, then
// crash-looped) — it was requesting up to 48 leaves' worth of NEW distinct
// decode work per solve with no lower cap for a constrained device.
const BOOST_REQUEST_CAP = 48;
// Was 12 (== MAX_REQUESTS_PER_SOLVE, i.e. zero actual boost on a low-memory
// device) while the crash's real cause was still unidentified. Raised now
// that it's fixed elsewhere (see ply-parse-worker.js's tryFastParse) — this
// was very likely part of why a freshly-navigated-to area could take ~30s
// to fully resolve on an iPhone 13 mini even after the budget fix (section
// 15): still meaningfully lower than desktop's 48, not restored all the way.
const BOOST_REQUEST_CAP_LOW_MEMORY = 24;
// Guaranteed minimum prewarm requests per solve, taken off the top of
// requestCap BEFORE real (on-screen) demand — see the issuance loop in
// _applyDesiredLevels. Prewarm candidates always sort last (their whole
// point is to never compete with real demand — see PREFETCH_DIST_BIAS), and
// even among themselves sort nearest-camera-first, so the single farthest
// things in the scene (typically distant sky) are the very last of the
// last. Under sustained real demand — e.g. continuous navigation, or a
// large lodDist making more content want fine detail — that queue position
// can mean literally never, not just slowly: confirmed live 2026-09-14,
// "the sky always stays grey" even once everything else had converged.
// This reserve guarantees steady forward progress every solve regardless of
// how much real demand exists; it does NOT cap prewarm to only this many
// when real demand is light — the issuance loop hands prewarm whatever's
// left of requestCap after real demand, down to this floor.
const PREWARM_MIN_PER_SOLVE = 3;
// Hard cap on prewarm's total issued footprint, as a fraction of budget —
// see the SplatOctree constructor's own comment for the full story of why
// this needs to be a real cap again (2026-09-15), not just "graceful alloc
// failure is protection enough".
const PREWARM_BUDGET_FRACTION = 0.25; // was 0.15 — capped before every coarse level was in (grey areas)
// How long a leaf that just failed pool.alloc() on its FIRST fill (nothing
// resident at all yet — res.level === -1) is left alone before it's
// eligible to be requested again. Found live 2026-09-15: a scene view with
// far more visible leaf regions than a constrained budget can represent
// even at pure coarsest (1050 visible vs a 1M budget) drove allocFailures
// into the tens of thousands — every failed leaf was re-requested, re-
// decoded, and re-failed on literally the very next solve, forever, wasting
// CPU/bandwidth on retries that couldn't succeed until something else
// freed up room, and very plausibly contributing to the low FPS observed
// alongside it. Only gates the never-resident-yet case (refining an
// already-resident leaf to a bigger level is a different, less severe
// failure mode not covered here).
const ALLOC_FAIL_BACKOFF_MS = 1000;
// A leaf whose finer level didn't fit the pool (budget / device limit /
// fragmentation) keeps what it has and isn't asked for that level again
// for this long — it used to be re-requested (and re-decoded) every solve,
// forever: 160+ failed allocations a second on a 1M phone budget, and the
// starting view never counted as loaded. See viewStatus().
const REFINE_FAIL_RETRY_MS = 3000;
// Wall-clock budget per frame for defrag slides (?defrag=1, exact-pack only
// — see SplatPoolExact/_processDefragQueue). Deliberately smaller than
// WRITE_BUDGET_MS: defrag is strictly lower priority than any real pending
// decode-driven write (see _processDefragQueue's own guard), so it should
// never meaningfully compete with normal LOD-change throughput even when
// there's a large backlog of slides queued.
const DEFRAG_BUDGET_MS = 2;
// How many currently-resident leaves to enqueue for a defrag slide each time
// an alloc() fails with genuine fragmentation detected (freeStats().totalFree
// covers the request but no single free interval does). Small and reactive
// on purpose for this first rollout — see the implementation plan's own
// judgment call on deferring a smarter/proactive trigger to a possible
// Stage 2 only if this proves insufficient on real devices.
const DEFRAG_BATCH = 8;
// Reference camera half-FOV for the FOV-scale factor — PlayCanvas uses
// tan(22.5°), i.e. a 45° vertical FOV as the neutral reference. A narrower
// effective FOV (a zoomed-in camera) scales the effective distance DOWN,
// so zooming in earns finer LOD even without moving closer.
const REF_TAN_HALF_FOV = Math.tan((22.5 * Math.PI) / 180);
// Fraction of pool capacity the LOD solver is allowed to target. The gap to
// 1.0 is allocator headroom: chunk sizes round up to a power of two (buddy
// blocks) and freed space isn't always the shape the next request wants.
// The buddy allocator coalesces so exhaustion is rare, and when it happens a
// leaf just holds its current LOD (no repack, no stall). 0.45 leaves a wide
// margin so alloc failures stay near zero even mid-navigation churn; raise
// ?budget= for more resident detail rather than pushing this up.
const USABLE_BUDGET_FRACTION = 0.45;
// With the exact-size allocator (SplatPoolExact — the default since
// 2026-10-03) there's no power-of-two rounding to leave headroom for, only
// hysteresis overshoot and fragmentation: target most of the pool.
const USABLE_BUDGET_FRACTION_EXACT = 0.85;
// Where a tile's content really is, for ranking refinement when the budget
// runs short (_solveBudgetScale's per-leaf plan). Box centres misled it:
// Cinevilla Forest Village v2's 32 m tiles reach far into the forest, so the
// tile holding half the house ranked like background and stayed at the
// coarsest level while the other half reached full detail (iPad, 2026-10-31).
// The first chunk to land for a tile leaves NEAR_SAMPLES of its splat centres;
// a tile ranks by the NEAR_PCT-th nearest of them (a quantile, not the
// minimum, so a few floaters near the camera don't pull a tile forward).
const NEAR_SAMPLES = 64;
const NEAR_PCT = 0.25;
// Idle refinement: once the camera has been still this long, the solver
// spends spare budget sharpening the whole view beyond the distance bands
// (scale < 1, down to MIN_REFINE_SCALE) instead of leaving it unused — so
// distant areas eventually reach the best detail the budget allows. The
// scale reached at rest is kept while moving (no demote/reload churn on
// every start/stop); it only coarsens again if the view exceeds budget.
const IDLE_REFINE_MS = 400;
// COVERAGE GUARANTEE (2026-10-03): a visible leaf must never stay empty.
// Visible-but-empty leaves are served first (before prewarm, refinement,
// demotes), uncapped by MAX_REQUESTS_PER_SOLVE; refinement waits until none
// are left; and if a coverage allocation still fails, off-screen leaves are
// evicted to make room and, failing that, the whole view's detail is lowered
// (this._pressure scales the usable budget down, recovering slowly once
// coverage allocations stop failing).
const PRESSURE_MIN = 0.3;
const PRESSURE_STEP_DOWN = 0.85;
const PRESSURE_STEP_UP = 1.03;
const PRESSURE_RECOVER_MS = 2000;
const EVICT_OFFSCREEN_AFTER_MS = 750;
const MIN_REFINE_SCALE = 1 / 64;
// Draw target (`visibleCap`, Splat.ts streamDrawCap — phones / tablets): plans
// the view to at most this many splats ON SCREEN (see _solveBudgetScale).
// A hard "distance bands only" floor before it left far areas thin (this
// model's coarse levels are thinned-out subsets).
// Hysteresis: a leaf's CURRENTLY resident level gets its own threshold
// widened by this factor before _desiredLevel will leave it. Without this,
// a camera hovering right at a level boundary — completely normal during
// ordinary mouse-drag navigation, not an edge case — flips that leaf
// between two levels on consecutive solves (every ~0.15+ world units of
// movement re-triggers one), each flip a real free+decode+alloc+write
// cycle. That reads as scattered "glitching spots" popping in and out
// while moving, and stops the instant the camera stops (matching what was
// reported) since re-solving itself stops.
// 2.2 (was 1.6): street-level navigation on a large scene parks many leaves
// near a band edge; a wider hold-band trades a little eagerness to sharpen
// for fewer per-object LOD flips mid-move.
const LOD_HYSTERESIS = 2.2;
// Re-run the cull+LOD+budget solve only after the camera has moved this far
// (world units) or turned this much (radians) since the last solve.
const REEVAL_POS_EPS = 0.15;
const REEVAL_ROT_EPS = 0.01;

function resolveUrl(base, rel) {
  return new URL(rel, base).toString();
}

// lod-meta.json bounds are in the SOG's own source coordinate space, but
// the actual rendered splat position is (px, -py, -pz) relative to that
// (see sog-chunk-worker.js's convertToGpuReady / Splat.ts's pushSOGData —
// centerAndScaleData stores -_center.y, and _center.z is already -pz by
// the time it's set). Every spatial decision the octree makes — frustum
// culling, camera-to-node distance, the initial camera framing — has to
// reason in that SAME rendered space, or it's culling/measuring against
// geometry that isn't where anything is actually drawn. Flipping a min/max
// pair also swaps which is which (negation reverses ordering).
function toRenderSpaceBox(min, max) {
  return new THREE.Box3(
    new THREE.Vector3(min[0], -max[1], -max[2]),
    new THREE.Vector3(max[0], -min[1], -min[2])
  );
}

// Merge small tiles (mergeTiles = minimum full-detail splats per tile, 0 =
// off). splat-transform 3.x cuts scenes into far more, far smaller tiles than
// 2.x (Cinevilla: 15.6k tiles × 8 levels vs 3.8k × 4) and every piece is a
// separate plan entry / download slice / GPU upload. Bottom-up, runs of
// neighbouring leaf siblings are joined into one leaf while their
// full-detail total is under the minimum, when at every kept level their data
// is one contiguous run in one file (true for every tree checked —
// splat-transform writes siblings next to each other). A merged leaf keeps
// the levels ALL its parts have (small tiles lose their coarsest levels in
// decimation; those hold a handful of splats), its box is the union.
const mergeBounds = (a, b) => ({ min: a.min.map((v, i) => Math.min(v, b.min[i])), max: a.max.map((v, i) => Math.max(v, b.max[i])) });
const finestEntry = (lods) => lods[String(Math.min(...Object.keys(lods).map(Number)))];
function joinTiles(group) {
  let levels = Object.keys(group[0].lods);
  for (const g of group) levels = levels.filter((l) => g.lods[l]);
  const finest = String(Math.min(...group.flatMap((g) => Object.keys(g.lods).map(Number))));
  if (!levels.includes(finest)) return null; // every part keeps its finest level
  const lods = {};
  for (const l of levels) {
    const runs = group.map((g) => g.lods[l]).sort((a, b) => a.offset - b.offset);
    if (runs.some((r) => r.file !== runs[0].file)) return null;
    let end = runs[0].offset;
    for (const r of runs) { if (r.offset !== end) return null; end += r.count; }
    lods[l] = { file: runs[0].file, offset: runs[0].offset, count: end - runs[0].offset };
  }
  return { bound: group.slice(1).reduce((b, g) => mergeBounds(b, g.bound), group[0].bound), lods };
}
function mergeSmallTiles(node, minSplats) {
  if (!node.children) return node;
  const children = node.children.map((c) => mergeSmallTiles(c, minSplats));
  const out = [];
  let run = [];
  const flush = () => {
    for (let i = 0; i < run.length;) {
      let group = [run[i]], joined = run[i], j = i + 1;
      while (j < run.length && group.reduce((n, g) => n + finestEntry(g.lods).count, 0) < minSplats) {
        const next = joinTiles([...group, run[j]]);
        if (!next) break;
        group = [...group, run[j]]; joined = next; j++;
      }
      out.push(joined); i = j;
    }
    run = [];
  };
  for (const c of children) {
    if (!c.children && c.lods) run.push(c);
    else { flush(); out.push(c); }
  }
  flush();
  return out.length === 1 ? out[0] : { ...node, children: out };
}

export function parseLodMeta(json, lodMetaUrl, { mergeTiles = 0 } = {}) {
  const baseUrl = lodMetaUrl; // URL() resolves relative to this directly
  const filenames = json.filenames.map((f) => resolveUrl(baseUrl, f));
  const leaves = [];
  const walk = (node) => {
    if (node.children) {
      node.children.forEach(walk);
    } else if (node.lods) {
      const box = toRenderSpaceBox(node.bound.min, node.bound.max);
      const center = box.getCenter(new THREE.Vector3());
      const radius = box.getSize(new THREE.Vector3()).length() / 2;
      const lods = {};
      for (const [level, entry] of Object.entries(node.lods)) {
        lods[Number(level)] = entry;
      }
      leaves.push({ box, center, radius, lods });
    }
  };
  walk(mergeTiles > 0 ? mergeSmallTiles(json.tree, mergeTiles) : json.tree);
  // json.tree.bound is the ROOT node's bound — the whole scene's extent.
  const bound = toRenderSpaceBox(json.tree.bound.min, json.tree.bound.max);

  // Content-weighted centre + radius. The raw bound is routinely inflated by
  // a handful of stray far-field ("sky") splats, so its geometric centre can
  // sit hundreds of units out in empty space — framing the camera there is
  // the "starts outside everything" bug. Weight each leaf's centre by its
  // finest-level splat count so the result tracks where the geometry
  // actually is; radius is the weighted RMS distance from that centre.
  const finestCount = (lods) => {
    const levels = Object.keys(lods).map(Number);
    if (!levels.length) return 1;
    const e = lods[Math.min(...levels)];
    return (e && e.count) || 1;
  };
  let cx = 0, cy = 0, cz = 0, wsum = 0;
  for (const lf of leaves) {
    const w = finestCount(lf.lods);
    cx += lf.center.x * w; cy += lf.center.y * w; cz += lf.center.z * w; wsum += w;
  }
  const contentCenter = wsum > 0
    ? new THREE.Vector3(cx / wsum, cy / wsum, cz / wsum)
    : bound.getCenter(new THREE.Vector3());
  let r2 = 0, rw = 0;
  for (const lf of leaves) {
    const w = finestCount(lf.lods);
    r2 += lf.center.distanceToSquared(contentCenter) * w; rw += w;
  }
  const contentRadius = rw > 0
    ? Math.max(1, Math.sqrt(r2 / rw))
    : (bound.getSize(new THREE.Vector3()).length() / 2) || 1;

  return {
    lodLevels: json.lodLevels,
    filenames,
    environment: json.environment ? resolveUrl(baseUrl, json.environment) : null,
    leaves,
    bound,
    contentCenter,
    contentRadius,
  };
}

// Small decode worker pool with a per-fileIndex result cache (a file often
// backs several leaf nodes) and in-flight de-duplication (so two leaves
// that both need a not-yet-cached file in the same solve pass trigger one
// decode, not two).
// A worker that has requests but sends nothing (no progress, no result) for
// this long is stuck — a download or Cache Storage call that never settles
// (seen on iPad Safari). Nothing else times out, and a worker runs its
// chunks one after another, so one such request blocked every later one on
// that worker, and the in-flight ceiling (16 on iPad, 2 workers) filled
// with requests that never ended: streaming stopped for good — no detail
// added whatever the budget. The watchdog replaces the worker and fails its
// requests; the octree retries them (the request's catch below). Each
// restart doubles the wait (up to DECODE_STALL_MAX_MS), so a merely slow
// connection can't be caught in a restart loop.
const DECODE_STALL_MS = 25000;
const DECODE_STALL_MAX_MS = 120000;
const DECODE_WATCHDOG_MS = 2000;
const FETCH_RATE_WINDOW_MS = 10000; // DecodePool.fetchStats() network rate window

class DecodePool {
  constructor(workerCount, shDegree, cacheTag = '', fileCoarse = []) {
    this.shDegree = shDegree;
    // LOD tree version (lod-meta.json ETag) — keys the chunk-file cache.
    this.cacheTag = cacheTag;
    // Per-file "coarse level" flag — such files are evicted from the chunk
    // cache last (see utils/lodChunkCache.js planPreload).
    this.fileCoarse = fileCoarse;
    this.workers = [];
    this._busy = []; // per worker: { pending: Map(fileIndex -> reject), lastAt } — see DECODE_STALL_MS
    for (let i = 0; i < workerCount; i++) this._startWorker(i);
    this._stallMs = DECODE_STALL_MS;
    this._watchdog = setInterval(() => this._checkStalled(), DECODE_WATCHDOG_MS);
    this._next = 0;
    this._cache = new Map(); // fileIndex -> decoded result
    this._inflight = new Map(); // fileIndex -> Promise<decoded result>
    this._progress = new Map(); // fileIndex -> { loaded, total, phase, at } while in flight
    this.fileRows = []; // splats per file (set by SplatOctree) — sizes the decode estimate
    this._msPerRow = 0; // measured decode speed this session (EMA); 0 = not yet measured
    // Where chunk time goes, for the DEBUG overlay's 'fetch' row: per-file
    // averages (EMA) of download, waiting for a free decoder once downloaded,
    // and decode; plus network bytes over the last FETCH_RATE_WINDOW_MS.
    this._fetch = { dlMs: 0, waitMs: 0, decMs: 0, files: 0, recent: [] };
  }

  // Overlay summary — see this._fetch. mbps counts network bytes only
  // (cache hits excluded), so it reads 0 once everything is cached.
  fetchStats() {
    const f = this._fetch, now = performance.now();
    while (f.recent.length && now - f.recent[0][0] > FETCH_RATE_WINDOW_MS) f.recent.shift();
    const bytes = f.recent.reduce((t, r) => t + r[1], 0);
    return { files: f.files, dlMs: f.dlMs, waitMs: f.waitMs, decMs: f.decMs, mbps: bytes / 1e6 / (FETCH_RATE_WINDOW_MS / 1000) };
  }

  _noteTiming(t) {
    const f = this._fetch, ema = (prev, v) => (f.files ? prev * 0.7 + v * 0.3 : v);
    f.dlMs = ema(f.dlMs, t.downloadMs || 0);
    f.waitMs = ema(f.waitMs, Math.max(0, t.queueWaitMs || 0));
    f.decMs = ema(f.decMs, t.decodeMs || 0);
    f.files++;
    if (t.networkBytes > 0) f.recent.push([performance.now(), t.networkBytes]);
  }

  _startWorker(i) {
    const worker = new Worker(new URL('../sog-chunk-worker.js', import.meta.url));
    const busy = { pending: new Map(), lastAt: performance.now() };
    // Any message from the worker (progress or a result) shows it's alive.
    worker.addEventListener('message', () => { busy.lastAt = performance.now(); });
    this.workers[i] = worker;
    this._busy[i] = busy;
  }

  _checkStalled() {
    const now = performance.now();
    this._busy.forEach((busy, i) => {
      if (busy.pending.size === 0 || now - busy.lastAt < this._stallMs) return;
      console.warn('[SplatOctree] decode worker stalled — restarting it', [...busy.pending.keys()]);
      this._stallMs = Math.min(this._stallMs * 2, DECODE_STALL_MAX_MS);
      this.stalls = (this.stalls || 0) + 1;
      this.workers[i].terminate();
      this._startWorker(i);
      for (const [fileIndex, reject] of busy.pending) {
        this._progress.delete(fileIndex);
        loadTrace.jobEnd(null);
        reject(new Error('decode stalled'));
      }
    });
  }

  // Resolves with { centerAndScale, covAndColor, sortXYZI, sh, numRows, hasSH }
  // for the whole file, from cache if already decoded.
  request(fileIndex, url) {
    if (this._cache.has(fileIndex)) return Promise.resolve(this._cache.get(fileIndex));
    if (this._inflight.has(fileIndex)) return this._inflight.get(fileIndex);

    // Least busy worker (fewest files queued on it), round-robin on ties: a
    // worker decodes its files one after another, so dealing them out in
    // turn left a file waiting behind a big decode while another worker
    // sat idle.
    let wi = this._next;
    for (let k = 1; k < this.workers.length; k++) {
      const j = (this._next + k) % this.workers.length;
      if (this._busy[j].pending.size < this._busy[wi].pending.size) wi = j;
    }
    const worker = this.workers[wi];
    const busy = this._busy[wi];
    this._next = (wi + 1) % this.workers.length;

    const promise = new Promise((resolve, reject) => {
      const onMessage = (e) => {
        if (e.data.fileIndex !== fileIndex) return; // this worker may be shared across requests
        if (e.data.type === 'progress') { this._progress.set(fileIndex, { ...e.data, at: performance.now() }); return; }
        worker.removeEventListener('message', onMessage);
        busy.pending.delete(fileIndex);
        loadTrace.jobEnd(e.data.type === 'decoded' ? (e.data.timing || {}) : null);
        const p = this._progress.get(fileIndex);
        if (p && p.phase === 'decode' && e.data.type === 'decoded' && e.data.numRows > 0) {
          const ms = (performance.now() - p.at) / e.data.numRows;
          this._msPerRow = this._msPerRow ? this._msPerRow * 0.7 + ms * 0.3 : ms;
        }
        this._progress.delete(fileIndex);
        if (e.data.type === 'decoded') {
          if (e.data.timing) this._noteTiming(e.data.timing);
          this._cache.set(fileIndex, e.data);
          resolve(e.data);
        } else {
          reject(new Error(e.data.message || 'decode failed'));
        }
      };
      worker.addEventListener('message', onMessage);
      // Idle until now: the stall clock starts with this request.
      if (busy.pending.size === 0) busy.lastAt = performance.now();
      busy.pending.set(fileIndex, reject);
      loadTrace.jobStart();
      worker.postMessage({ fileIndex, url, shDegree: this.shDegree, cacheTag: this.cacheTag, coarse: !!this.fileCoarse[fileIndex], query: getAccessQuery() });
    });
    this._inflight.set(fileIndex, promise);
    promise.finally(() => this._inflight.delete(fileIndex));
    return promise;
  }

  // 0..1 how far along a requested file is: download up to 0.8, decoding
  // 0.8-0.95 by elapsed time against this session's measured decode speed
  // (a large chunk on a phone takes seconds), 1 once decoded (waiting to
  // be written).
  progressOf(fileIndex) {
    if (this._cache.has(fileIndex)) return 1;
    const p = this._progress.get(fileIndex);
    if (!p) return 0;
    if (p.phase === 'decode') {
      // Before the first measurement: ~4 µs a splat (a phone-class guess).
      const expected = (this.fileRows[fileIndex] || 100000) * (this._msPerRow || 0.004);
      return 0.8 + 0.15 * Math.min(1, (performance.now() - p.at) / Math.max(1, expected));
    }
    return p.total > 0 ? 0.8 * Math.min(1, p.loaded / p.total) : 0;
  }

  // Synchronous lookup for a file already decoded (used by SplatOctree's
  // compaction pass, which only ever repacks already-resident leaves —
  // their file is guaranteed to be in cache already).
  getCached(fileIndex) {
    return this._cache.get(fileIndex);
  }

  // Drop cached decodes for files nothing currently needs, keeping at most
  // `max` total. `keep` is the set of fileIndexes backing a resident or
  // in-flight leaf — those are never evicted (compaction re-slices them).
  // Without this the cache grows unbounded: every chunk ever streamed stays
  // decoded in memory (~20-50 MB each), which OOMs a long city walk.
  evict(keep, max) {
    if (this._cache.size <= max) return;
    for (const fileIndex of this._cache.keys()) {
      if (this._cache.size <= max) break;
      if (!keep.has(fileIndex)) this._cache.delete(fileIndex);
    }
  }

  destroy() {
    clearInterval(this._watchdog);
    this.workers.forEach((w) => w.terminate());
    this._cache.clear();
  }
}

export class SplatOctree {
  /**
   * @param {ReturnType<typeof parseLodMeta>} meta
   * @param {{ pool: import('./splatPool').SplatPool, sortWorker: Worker, decodeWorkerCount?: number, lowMemory?: boolean }} deps
   */
  constructor(meta, { pool, sortWorker, decodeWorkerCount = 4, lodDist = 0, lodMult = 0, prewarm = true, lowMemory = false, knapsack = false, defrag = false, visibleCap = Infinity }) {
    // Device-scaled decode cache / write queue / catch-up-burst request cap
    // — see each constant's own comment. Plain instance properties (not the
    // module consts directly) so the constructor is the one place that
    // needs to know about device class at all; everywhere else just reads
    // these.
    this._maxDecodeCache = lowMemory ? MAX_DECODE_CACHE_LOW_MEMORY : MAX_DECODE_CACHE;
    this._maxWriteQueue = lowMemory ? MAX_WRITE_QUEUE_LOW_MEMORY : MAX_WRITE_QUEUE;
    this._boostRequestCap = lowMemory ? BOOST_REQUEST_CAP_LOW_MEMORY : BOOST_REQUEST_CAP;
    this.meta = meta;
    this.pool = pool;
    this._pressure = 1; // budget pressure multiplier — see PRESSURE_MIN
    // Finest LOD scale idle refinement may reach (see DETAIL_FLOOR_LOW_MEMORY);
    // setDetail(pct) scales it coarser: scale floor = base × 100 / pct.
    this._detailBase = MIN_REFINE_SCALE;
    this._visibleCap = visibleCap > 0 ? visibleCap : Infinity;
    this.detailPct = 100;
    this._detailFloor = this._detailBase;
    this._levelFloor = 0; // finest level any leaf may take — see _solveBudgetScale
    this._floorRelief = new Map(); // leaf -> finer level allowed under _levelFloor
    this._visibleEmpty = 0;
    this.budget = 0; // real value set by setBudget() right after construction
    this.sortWorker = sortWorker;
    this.decodePool = new DecodePool(decodeWorkerCount, pool.shDegree, meta.cacheTag || '', meta.fileCoarse || []);
    for (const leaf of meta.leaves) {
      for (const e of Object.values(leaf.lods)) {
        if (e && e.file != null) this.decodePool.fileRows[e.file] = (this.decodePool.fileRows[e.file] || 0) + (e.count || 0);
      }
    }

    // ?knapsack=1 (Part A of the "match PlayCanvas" rewrite): replaces the
    // legacy global distance-scale search (_solveBudgetScale) with a
    // per-leaf value-ranked greedy allocator (_solveKnapsack) for VISIBLE
    // leaves specifically. Off by default for Stage 1 — see
    // /Users/uldis.liepins/.claude/plans/floating-launching-blum.md.
    this._knapsack = knapsack === true;
    // ?exactPack=1 (Part B): a SplatPoolExact instead of the buddy-allocator
    // SplatPool has no power-of-two rounding, so budget accounting should
    // use RAW splat counts, not nextBucket()'s rounded-up bucket size — using
    // nextBucket() against an exact pool would make the solver needlessly
    // pessimistic (targeting less detail than the pool can actually hold).
    // Computed once here rather than checked per-call; nextBucket is a
    // no-op-cost passthrough when the pool doesn't round at all.
    this._bucketFn = pool.exact ? (count) => count : nextBucket;
    // ?defrag=1 (Part B, only meaningful alongside ?exactPack=1 — see
    // _processDefragQueue): reactively slides a small, bounded number of
    // already-resident leaves to consolidate free space after an alloc()
    // fails due to fragmentation (freeStats().totalFree covers the request
    // but no single interval does). Reuses the exact same 'move' sort-worker
    // message every normal LOD swap already sends — see STREAMING-SOG-
    // STATUS.md section 5 for why that matters (a prior attempt at this used
    // a NEW 'relocate' message and badly corrupted chunk positions).
    this._defragEnabled = defrag === true && !!pool.exact;
    this._defragQueue = []; // leafIndex[], FIFO
    this._defragQueued = new Set(); // dedupe membership for the queue above
    this._defragMoves = 0;

    // baseDistance scales with the CHUNK size (average leaf radius). The
    // distance metric it's compared against (see _desiredLevel) is the
    // distance from the camera to the NEAREST POINT ON a leaf's bounding
    // box — 0 when the camera is inside it — matching PlayCanvas's own
    // GSplatOctreeInstance.evaluateNodeLods (which clamps the camera
    // position into each node's AABB before measuring). Measuring to the
    // box CENTER instead — the earlier approach here — added a constant
    // ~chunk-radius offset to every nearby chunk's distance, pushing it 1-2
    // LOD levels coarser than warranted: close-ups never sharpened and a
    // wide shot sat mostly at the coarsest level even where chunk surfaces
    // were right next to the camera. PlayCanvas's default base is a fixed
    // 5 units with multiplier 3; deriving base from chunk radius keeps it
    // sane across scene scales, and ~0.7 * a ~7-unit average radius lands
    // near that same 5.
    const leafRadii = meta.leaves.map((l) => l.radius).filter((r) => r > 0);
    const avgLeafRadius = leafRadii.length
      ? leafRadii.reduce((a, b) => a + b, 0) / leafRadii.length
      : (meta.bound.getSize(new THREE.Vector3()).length() / 2) || 1;
    // ?lodDist= (world units) and ?lodMult= override the auto values so a
    // scene can be tuned by eye — see SceneHandler's streaming branch.
    this.baseDistance = lodDist > 0 ? lodDist : avgLeafRadius * CHUNK_RADIUS_MULTIPLE;
    this.lodMultiplier = lodMult > 0 ? lodMult : LOD_MULTIPLIER;
    // The model's full-detail splat count: every leaf at its finest level.
    this.modelSplats = meta.leaves.reduce((sum, leaf) => {
      const levels = Object.keys(leaf.lods || {}).map(Number);
      const finest = levels.length ? leaf.lods[Math.min(...levels)] : null;
      return sum + ((finest && finest.count) || 0);
    }, 0);
    window.__splatOctreeStats = { ...window.__splatOctreeStats, baseDistance: +this.baseDistance.toFixed(1), lodMultiplier: this.lodMultiplier, poolCapacity: pool.capacity, modelSplats: this.modelSplats };
    this._camPosScratch = new THREE.Vector3();
    window.__splatOctree = this; // dev bridge for the Quality modal's live tuning sliders
    // Catch-up boost on load: fill the starting view fast (higher per-frame
    // request/apply budgets) instead of trickling it in — the user is
    // waiting on first paint, not navigating yet.
    this._boostUntil = performance.now() + 4000;
    this._wantsMoreDetail = true;
    this._knapsackHasPendingWork = false; // see _solveKnapsack's comment

    // Per-leaf resident state, parallel to meta.leaves. `pendingLevel`
    // tracks a level currently being fetched (-1 = none in flight) — see
    // _applyDesiredLevels/_fulfillRequests for why this is needed both to
    // avoid a visible gap on LOD swaps and to avoid re-requesting a level
    // that's already in flight.
    // fileIndex/srcOff are carried alongside so a full repack (_compact) can
    // re-slice each live leaf's data straight from the decode cache without
    // needing to re-fetch or re-decode anything. everVisible tracks whether
    // this leaf has EVER actually been in the camera's frustum — separate
    // from lastVisibleAt, which only means "was visible as of the last time
    // it was" and is otherwise 0/undefined, indistinguishable from "just
    // prewarmed, never actually seen" — see the prewarm comment below for
    // why that distinction is load-bearing.
    this.resident = meta.leaves.map(() => ({ level: -1, offset: -1, count: 0, pendingLevel: -1, fileIndex: -1, srcOff: 0, everVisible: false, allocFailAt: 0, failLevel: -1, failAt: 0, failCount: 0 }));
    // Per leaf: NEAR_SAMPLES splat centres (render space, xyz), set by the
    // first chunk written for it — see NEAR_SAMPLES.
    this._nearSamples = new Array(meta.leaves.length).fill(null);
    this._nearDistScratch = new Float32Array(NEAR_SAMPLES);

    // ─── Coarse-first prewarm ───────────────────────────────────────────────
    // On by default (?prewarm=0 disables). After PlayCanvas's own documented
    // Streamed-SOG behaviour (developer.playcanvas.com's Gaussian Splatting
    // docs, researched 2026-09-14): "downloads lowest LODs first... appears
    // almost instantly... sharpening progressively." Requests every leaf's
    // coarsest level up front, not just leaves in the current frustum, so
    // the WHOLE scene shows a blurry-but-correct shell immediately instead
    // of the grey clear colour wherever nothing has streamed in yet — the
    // normal distance-prioritised solver still refines real detail on top
    // exactly as it already did.
    //
    // A first version of this (2026-09-14, see STREAMING-SOG-STATUS.md
    // section 9) ALSO made every resident leaf permanently unfreeable, which
    // regressed real captures: far/never-visible leaves lost the
    // pool.alloc() race forever once the pool stayed fuller than before at
    // all times. This version doesn't touch residency/freeing rules at
    // all — a prewarmed leaf is culled by the EXACT same rules as any other
    // UNLESS it's never actually been visible (res.everVisible, set above),
    // in which case freeNow/demoteNow below simply don't apply to it. It
    // just sits at its coarsest allocation indefinitely once filled — see
    // below for why that's NOT assumed to be cheap for every scene shape.
    // Hard cap on how much of the budget prewarm is ever allowed to
    // ISSUE requests for, tracked as they're issued (see the issuance loop
    // in _applyDesiredLevels) — once reached, prewarm stops asking for more
    // and whatever's left un-prewarmed (typically the very farthest things,
    // e.g. distant sky) just stays grey until the camera actually reaches
    // it, same as pre-prewarm behaviour. This existed once before as a
    // soft SUBTRACTION from the near-camera solver's usable target instead
    // (a paper reservation) and was dropped 2026-09-14 on the theory that
    // graceful alloc failure alone was protection enough — that reasoning
    // silently depended on a bug (see the res.pendingLevel fix above/
    // STREAMING-SOG-STATUS.md section 17): before that fix, a decoded
    // prewarm chunk was thrown away in _fulfillRequests BEFORE it ever
    // reached pool.alloc(), so the pool never actually filled up with
    // prewarm's own allocations no matter how many leaves it tried. Once
    // that bug was fixed and prewarm's requests started actually landing,
    // it could genuinely consume a large share of a small mobile budget —
    // confirmed live: near-camera detail got WORSE, not just the sky
    // unaffected, immediately after the pendingLevel fix landed. A hard
    // issuance cap (this time, not a soft subtraction) bounds prewarm's
    // worst case directly without the "reservation can exceed the whole
    // budget on paper" problem the subtraction version had for a
    // many-small-leaves scene. Computed off this.budget lazily (in the
    // issuance loop, via PREWARM_BUDGET_FRACTION below) rather than cached
    // here — this.budget isn't actually set yet at construction time
    // (setBudget() runs right after — see the caller in Splat.ts), and
    // staying lazy also means a live budget change (the Quality modal's
    // streaming sliders call setTuning) is picked up automatically.
    this._prewarm = prewarm !== false;
    this._coarsest = meta.lodLevels - 1;
    this._prewarmBudgetUsed = 0;
    this._prewarmPending = this._prewarm;

    // Finished-but-not-yet-uploaded decodes; drained a few per frame by
    // _processWriteQueue so a burst of completions can't spike a frame.
    this._writeQueue = [];
    // Requests dispatched to a decode worker but not yet enqueued/resolved.
    // Counted alongside _writeQueue.length against MAX_WRITE_QUEUE so a fine
    // octree can't dispatch thousands of decodes before the first returns.
    this._pendingDecodes = 0;

    this._lastCamPos = new THREE.Vector3(NaN, NaN, NaN);
    this._lastCamQuat = new THREE.Quaternion();
    this._frustum = new THREE.Frustum();
    this._projScreenMatrix = new THREE.Matrix4();
    // Predictive prefetch: smoothed camera velocity + the point it projects
    // to PREFETCH_SECONDS ahead. Leaves near that point but not yet visible
    // get their coarsest level requested early so they're resident before
    // the camera arrives (instead of resolving on screen as you watch).
    this._velPrevPos = new THREE.Vector3(NaN, NaN, NaN);
    this._velPrevT = 0;
    this._camVel = new THREE.Vector3();
    this._prefetchPoint = new THREE.Vector3();

    // Debug/verification surface — mirrors the existing
    // window.__splatDebugStats convention used by Splat.ts.
    window.__splatOctreeStats = {
      ...window.__splatOctreeStats,
      residentSplats: 0, residentLeaves: 0, visibleLeaves: 0, budget: this.budget,
      knapsackMode: this._knapsack ? 'knapsack' : 'legacy',
      poolMode: pool.exact ? 'exact' : 'buddy',
      defragEnabled: this._defragEnabled,
    };
  }

  setBudget(budget) {
    this.budget = budget;
    window.__splatOctreeStats = { ...window.__splatOctreeStats, budget, budgetMax: this.budgetMax || this.pool.capacity };
  }

  /** Live tuning from the Quality modal — no reload. budget is the solver's
   * target (soft cap); it's clamped to the pool's fixed capacity, which was
   * sized at load time and can only grow with a reload. lodDist/lodMult
   * take effect on the next solve. Passing 0/undefined for a field leaves
   * it unchanged. */
  setTuning({ budget, lodDist, lodMult } = {}) {
    // Capped at budgetMax (the device max / model size — the pool itself
    // is a bit larger, as headroom for chunk swaps; see loadStreamed).
    if (budget > 0) this.budget = Math.min(budget, this.budgetMax || this.pool.capacity);
    if (lodDist > 0) this.baseDistance = lodDist;
    if (lodMult > 0) this.lodMultiplier = lodMult;
    window.__splatOctreeStats = {
      ...window.__splatOctreeStats,
      budget: this.budget,
      baseDistance: +this.baseDistance.toFixed(1),
      lodMultiplier: this.lodMultiplier,
      poolCapacity: this.pool.capacity,
      budgetMax: this.budgetMax || this.pool.capacity,
    };
    // Force the next update() to re-solve even if the camera hasn't moved.
    this._lastCamPos.set(NaN, NaN, NaN);
    // Catch-up boost: for the next ~2s let the solver issue and apply far
    // more LOD changes per frame than the steady-state throttle allows, so a
    // slider drag converges in a second or two instead of trickling in.
    // Safe to be aggressive here — the user is deliberately waiting on this
    // change, not navigating, so a brief frame-rate dip is the right trade.
    this._boostUntil = performance.now() + 2200;
  }

  /** Adaptive quality's detail lever: 100 = device default, 50 = coarser
   * everywhere — the draw target (visibleCap) × pct, and the refinement floor
   * log-interpolated from MIN_REFINE_SCALE to distance bands ×2. Re-solves
   * at once. */
  setDetail(pct) {
    const p = Math.max(50, Math.min(100, Math.round(pct)));
    if (p === this.detailPct) return;
    this.detailPct = p;
    const t = (p - 50) / 50; // 1 at 100%, 0 at 50%
    this._detailFloor = Math.pow(2, 1 - t) * Math.pow(this._detailBase, t);
    this._refinedAtRest = false;
    this._lastCamPos.set(NaN, NaN, NaN);
  }

  /** True while the loading overlay is up — GPU uploads get a bigger share
   * of each frame (WRITE_BUDGET_MS_LOADING). */
  setLoading(on) {
    this._loading = !!on;
  }

  /** Draw target (Quality menu's Performance switch) — Infinity = off.
   * Re-solves at once. */
  setVisibleCap(cap) {
    const c = cap > 0 ? cap : Infinity;
    if (c === this._visibleCap) return;
    this._visibleCap = c;
    this._refinedAtRest = false;
    this._lastCamPos.set(NaN, NaN, NaN);
  }

  _boosting() {
    return this._boostUntil && performance.now() < this._boostUntil;
  }

  /** Current view's load state, for the initial-load overlay. Computed on
   * demand from the last solve's visible leaves and their wanted levels.
   * `fraction` — splats resident in view (plus partial credit for a chunk
   * still downloading/decoding — DecodePool.progressOf) over the view's
   * expected final amount: before the at-rest refinement has run that's
   * the view's full-detail size capped at the usable budget (an estimate —
   * the refinement adds detail toward it), after, the real targets. One
   * measure across both passes, so neither gets a fixed share it may not
   * need. `complete` — nothing left to do for the view. A leaf whose wanted
   * level didn't fit the pool (failCount >= 2 — budget/device limit)
   * counts as done: that's the best this device can show. `limited` — how
   * many leaves that applies to. The first pass fills the bar up to
   * LOAD_FIRST_PASS_SHARE, the refinement the rest. */
  viewStatus() {
    const targets = this._viewTargets;
    if (!targets) return { fraction: 0, complete: false, limited: 0, want: 0 };
    let want = 0, have = 0, finest = 0, pending = 0, limited = 0;
    for (const [leafIndex, target] of targets) {
      const leaf = this.meta.leaves[leafIndex];
      const res = this.resident[leafIndex];
      const w = (leaf.lods[target] && leaf.lods[target].count) || 0;
      const fine = leaf.lods[0] || leaf.lods[Math.min(...Object.keys(leaf.lods).map(Number))];
      finest += (fine && fine.count) || w;
      want += w;
      const resident = res.level >= 0 ? res.count : 0;
      // Lower level number = finer: at or finer than wanted is done.
      if (res.level >= 0 && res.level <= target && res.pendingLevel === -1) { have += w; continue; }
      // Limited: counted as fully there — it won't get any closer.
      if (res.failCount >= 2 && (res.level === -1 || res.failLevel <= target)) { have += w; limited++; continue; }
      pending++;
      const pendingEntry = res.pendingLevel >= 0 ? leaf.lods[res.pendingLevel] : null;
      const p = pendingEntry ? this.decodePool.progressOf(pendingEntry.file) : 0;
      have += resident + Math.max(0, (pendingEntry ? pendingEntry.count : w) - resident) * p * 0.95;
    }
    const refined = this._knapsack || this._refinedAtRest;
    const usable = Math.min(this.budget * (this.pool.exact ? USABLE_BUDGET_FRACTION_EXACT : USABLE_BUDGET_FRACTION),
      this._visibleCap) * (this.detailPct / 100); // same goal the solver plans to
    const goal = refined ? want : Math.max(want, Math.min(finest, usable));
    // Not refined but nothing pending and some chunks at the device limit:
    // refinement can't start (it waits for every visible chunk to have
    // data), and there's nothing else coming — done.
    const complete = pending === 0 && !this._deferredUpgrades && !this._viewStale && (refined || limited > 0);
    // Half splats, half chunks: a chunk waiting for its GPU upload is all
    // there by splats but still work to do — counting chunks keeps the
    // upload tail of a load moving instead of parked in the high 90s.
    const leaves = targets.length || 1;
    const passFrac = 0.5 * (goal > 0 ? Math.min(1, have / goal) : 0) + 0.5 * (1 - pending / leaves);
    let fraction;
    if (complete) fraction = 1;
    else if (this._knapsack) fraction = Math.min(0.99, passFrac);
    else if (!refined) { fraction = LOAD_FIRST_PASS_SHARE * passFrac; this._refinePendingStart = null; }
    else {
      // The refinement's own progress: chunks left of those it started with.
      if (this._refinePendingStart == null) this._refinePendingStart = Math.max(1, pending);
      const refineFrac = 1 - Math.min(1, pending / this._refinePendingStart);
      fraction = Math.min(0.99, LOAD_FIRST_PASS_SHARE + (1 - LOAD_FIRST_PASS_SHARE) * refineFrac);
    }
    return { fraction, complete, limited, want };
  }

  /** Call once per frame, before rendering. No-ops until the camera has
   * moved past the re-evaluation threshold. */
  update(camera) {
    this.pool.tick(); // advance the freed-slot quarantine every frame, moved or not
    const writeStart = loadTrace.enabled && this._writeQueue.length ? performance.now() : 0;
    this._processWriteQueue(); // apply a bounded number of finished decodes, every frame
    if (writeStart) loadTrace.write(performance.now() - writeStart);
    this._processDefragQueue(); // apply a bounded number of defrag slides, strictly lower priority than the above

    // Track smoothed camera velocity every frame (not gated by the solve
    // throttle below) so the prefetch point is always fresh.
    const nowT = performance.now();
    if (!Number.isNaN(this._velPrevPos.x) && this._velPrevT) {
      const dt = Math.max(1e-3, (nowT - this._velPrevT) / 1000);
      this._camVel.lerp(this._camPosScratch.copy(camera.position).sub(this._velPrevPos).multiplyScalar(1 / dt), 0.3);
    }
    this._velPrevPos.copy(camera.position);
    this._velPrevT = nowT;
    this._prefetchPoint.copy(camera.position).addScaledVector(this._camVel, PREFETCH_SECONDS);

    const moved = camera.position.distanceTo(this._lastCamPos) > REEVAL_POS_EPS;
    const turned = !moved && camera.quaternion.angleTo(this._lastCamQuat) > REEVAL_ROT_EPS;
    // Keep solving while there's unfinished streaming work even if the camera
    // is dead still: decodes in flight, writes queued, or the last solve
    // still had visible leaves it couldn't fully serve (request cap / queue
    // ceiling). Without this, a fresh load only ever gets the handful of
    // chunks the FIRST solve requests, then freezes with grey blocks until
    // you happen to move the camera. `_wantsMoreDetail` is cleared by a
    // solve that finds nothing left to request.
    if (moved || turned || !this._lastMoveAt) { this._lastMoveAt = nowT; this._refinedAtRest = false; }
    // Refine at rest only once nothing visible is empty — coverage first.
    const idle = nowT - this._lastMoveAt >= IDLE_REFINE_MS && !(this._visibleEmpty > 0);
    const refinePending = idle && !this._refinedAtRest;
    const failRetryDue = this._failRetryAt > 0 && nowT >= this._failRetryAt; // see REFINE_FAIL_RETRY_MS
    const planRecheckDue = this._planRecheckAt > 0 && nowT >= this._planRecheckAt; // see _solveBudgetScale
    const busy = this._viewStale || failRetryDue || planRecheckDue || this._writeQueue.length > 0 || this._pendingDecodes > 0 || this._wantsMoreDetail || this._deferredUpgrades || this._prewarmPending || refinePending || this._visibleEmpty > 0 || (this._pressure || 1) < 1;
    // Nothing left to fetch for the current view (Splat.ts's background
    // preload waits for this, so it never competes with visible chunks).
    this.streamingIdle = !(this._writeQueue.length > 0 || this._pendingDecodes > 0 || this._wantsMoreDetail
      || this._deferredUpgrades || this._prewarmPending || this._visibleEmpty > 0);
    if (this.streamingIdle) loadTrace.idle(); // ?loadTrace=1: prints the summary once
    if (this.pool.residentCount > 0) loadTrace.firstSplats();
    if (!moved && !turned && !Number.isNaN(this._lastCamPos.x) && !this._boosting() && !busy) return;
    this._lastCamPos.copy(camera.position);
    this._lastCamQuat.copy(camera.quaternion);

    camera.updateMatrixWorld();
    this._projScreenMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this._frustum.setFromProjectionMatrix(this._projScreenMatrix);

    // FOV-scale factor (PlayCanvas evaluateNodeLods): a narrower effective
    // vertical FOV — a zoomed-in camera — scales the distance metric DOWN,
    // so zooming in alone earns finer LOD. camera.zoom folds in this
    // codebase's custom dolly-free zoom. Fall back to 1 for an orthographic
    // or oddly-configured camera.
    const fov = camera.isPerspectiveCamera ? camera.fov : 45;
    const zoom = camera.zoom || 1;
    this._fovScale = Math.tan((fov * 0.5 * Math.PI) / 180) / zoom / REF_TAN_HALF_FOV || 1;

    // Budget pressure recovers slowly once coverage allocations stop failing.
    if (this._pressure < 1 && nowT - (this._lastCoverageFailAt || 0) > PRESSURE_RECOVER_MS) {
      this._pressure = Math.min(1, this._pressure * PRESSURE_STEP_UP);
    }
    // Adaptive quality's detail lever scales what's planned while MOVING
    // only. At rest the whole budget is planned: frames stop being drawn when
    // nothing changes, so a still view costs no frame rate, and with the
    // lever applied at rest it never came back up — adaptive quality cut v2
    // to 62% on an iPad (fps under 20 while moving), then planned 1.8M of a
    // 3.5M budget and half the house stayed coarse for good (2026-10-31).
    // While moving, what's already loaded is held up to the full budget
    // (holdUsable) — no demote churn on every start — and the lever only
    // limits detail added on top.
    const fullUsable = this.budget * (this.pool.exact ? USABLE_BUDGET_FRACTION_EXACT : USABLE_BUDGET_FRACTION) * (this._pressure || 1);
    const detail = idle ? 100 : this.detailPct;
    const budgetUsable = fullUsable * (detail / 100);
    // The draw target counts on-screen splats only (see _solveBudgetScale).
    const drawUsable = this._visibleCap * (detail / 100);
    const usable = Math.min(budgetUsable, drawUsable);
    const hold = idle ? null : { usable: fullUsable, draw: this._visibleCap };
    if (this._knapsack) {
      const desiredMap = this._solveKnapsack(camera, usable);
      this._applyDesiredLevels(camera, 1, desiredMap);
    } else {
      // At rest: allow refining past the distance bands; moving: keep the
      // detail reached at rest (this._restScale), coarsening only if over.
      const minScale = idle
        ? Math.max(MIN_REFINE_SCALE, this._detailBase)
        : (this._restScale || Math.max(1, this._detailFloor));
      const scale = this._solveBudgetScale(camera, budgetUsable, minScale, drawUsable, hold);
      // Why the last solve chose what it did — the DEBUG overlay's 'plan' row.
      this.lastPlan = { scale, minScale, usable: budgetUsable, drawUsable, pressure: this._pressure || 1, detail,
        floor: this._levelFloor, relief: this._floorRelief.size, idle, visibleEmpty: this._visibleEmpty || 0, fov: this._fovScale };
      if (idle) { this._restScale = Math.min(1, scale); this._refinedAtRest = true; }
      this._applyDesiredLevels(camera, scale, null, idle);
    }
  }

  // Binary-searches a threshold multiplier (>=1) that pushes nodes to
  // coarser LODs until the total splat count at each visible node's
  // resulting desired level fits `usable`. A no-op (scale=1) already fits
  // most of the time; the search only does real work once the scene is
  // dense enough for the camera's current view to exceed budget.
  //
  // Deliberately evaluates _desiredLevel WITHOUT hysteresis (no currentLevel
  // passed) here, even though _applyDesiredLevels below uses it WITH
  // hysteresis for the leaves it actually requests. Hysteresis makes a
  // leaf's CURRENT level resist coarsening — which is exactly what this
  // search's whole job is; feeding that resistance back into the same
  // search that's supposed to overcome it meant a crowded view could make
  // every trial scale look like it still didn't fit, all the way up to the
  // search's own upper bound, which was then returned anyway with no check
  // that it actually satisfied `usable`. That's a solver that can silently
  // stop converging, not a marginal overshoot — and it read as exactly what
  // was reported: the same few dozen leaves failing pool.alloc() every
  // single cycle, forever, regardless of camera position. The search needs
  // to reason about true, sticky-free demand to guarantee an answer that
  // actually fits; the small extra given back to already-resident leaves by
  // hysteresis in _applyDesiredLevels afterward is bounded and is what
  // USABLE_BUDGET_FRACTION's margin is for.
  // `minScale` (<= 1) lets the search go FINER than the distance bands when
  // the budget has room (idle refinement — see IDLE_REFINE_MS): it returns
  // the smallest scale in [minScale, 64] whose total fits `usable`.
  // drawUsable (the draw target, Infinity = none) bounds the splats estimated
  // to land ON SCREEN: each visible leaf's count × the share of its box's
  // projected outline inside the view, and none for recently-seen leaves
  // behind the camera. Counting whole frustum-touching boxes (big far boxes
  // mostly off screen) plus those recent leaves made any target coarsen the
  // view to well under half of it (2026-10-23: 1.2M–1.6M all drew ~0.78M).
  _solveBudgetScale(camera, usable, minScale = 1, drawUsable = Infinity, hold = null) {
    const now = performance.now();
    const maxLevel = this.meta.lodLevels - 1;
    const fovScale = this._fovScale || 1;
    // Precompute the scale=1 distance band edges once.
    const band = this._bandScratch || (this._bandScratch = []);
    for (let l = 0; l <= maxLevel; l++) band[l] = this.baseDistance * Math.pow(this.lodMultiplier, l);

    // ONE pass over every leaf: the frustum test + nearest-surface distance
    // are the expensive per-leaf ops and they don't depend on `scale`, so do
    // them here, not inside the binary search's 10 iterations. At tens of
    // leaves that didn't matter; at a city-scale tree (10k+ leaves) redoing
    // them 10x was tens of ms per solve — the jank. The off-screen-grace
    // cost is also scale-independent, so fold it into a fixed sum now.
    let fixedSum = 0, fixedCoarse = 0, fixedExpiry = Infinity;
    const visDist = (this._visDistScratch || (this._visDistScratch = []));
    const visLods = (this._visLodsScratch || (this._visLodsScratch = []));
    const visShare = (this._visShareScratch || (this._visShareScratch = []));
    const visLeaf = (this._visLeafScratch || (this._visLeafScratch = []));
    const visIdx = (this._visIdxScratch || (this._visIdxScratch = []));
    const capped = Number.isFinite(drawUsable);
    let vn = 0;
    const leaves = this.meta.leaves;
    for (let i = 0; i < leaves.length; i++) {
      const leaf = leaves[i];
      if (this._frustum.intersectsBox(leaf.box)) {
        visDist[vn] = leaf.box.distanceToPoint(camera.position) * fovScale;
        visLods[vn] = leaf.lods;
        visShare[vn] = capped ? this._onScreenShare(leaf.box) : 1;
        visLeaf[vn] = leaf;
        visIdx[vn] = i;
        vn++;
        continue;
      }
      const res = this.resident[i];
      if (res.level >= 0 && now - (res.lastVisibleAt || 0) <= CULL_FREE_MS && leaf.lods[res.level]) {
        fixedSum += this._bucketFn(leaf.lods[res.level].count);
        fixedCoarse += this._bucketFn((this._coarseEntry(leaf) || { entry: leaf.lods[res.level] }).entry.count);
        fixedExpiry = Math.min(fixedExpiry, (res.lastVisibleAt || 0) + CULL_FREE_MS);
      }
    }

    // this._bucketFn(), not raw count: the (buddy-allocator) pool allocates
    // power-of-two BUCKETS (SplatPool.alloc), so a 46k-splat chunk consumes a
    // 65536-slot bucket. Summing raw counts told the solver more detail fit
    // than the pool could hold — the allocator then failed constantly and
    // fell back to stale/no data ("quality never improves"). Under
    // ?exactPack=1, _bucketFn is a raw-count passthrough (no rounding to
    // account for) — see the constructor's comment.
    let drawSum = 0; // on-screen estimate of the last total() call
    let floor = 0; // level floor under trial — see the end of this function
    const total = (scale) => {
      let sum = fixedSum;
      drawSum = 0;
      for (let k = 0; k < vn; k++) {
        const d = visDist[k];
        const lods = visLods[k];
        let level = maxLevel;
        for (let l = 0; l <= maxLevel; l++) {
          if (d < band[l] / scale) { level = l; break; }
        }
        if (level < floor) level = floor;
        let entry = lods[level];
        if (!entry) {
          for (let l = level + 1; l <= maxLevel && !entry; l++) entry = lods[l];
          for (let l = level - 1; l >= 0 && !entry; l--) entry = lods[l];
        }
        if (entry) { sum += this._bucketFn(entry.count); drawSum += entry.count * visShare[k]; }
      }
      return sum;
    };
    // While moving (hold set) the distance-band plan keeps the full budget:
    // minScale is already the scale reached at rest, so this holds it.
    const bandUsable = hold ? hold.usable : usable, bandDraw = hold ? hold.draw : drawUsable;
    const fits = (scale) => total(scale) <= bandUsable && drawSum <= bandDraw;
    const search = () => {
      if (fits(minScale)) return minScale;
      let lo = minScale, hi = 64;
      for (let i = 0; i < 12; i++) {
        const mid = Math.sqrt(lo * hi);
        if (fits(mid)) hi = mid; else lo = mid;
      }
      return hi;
    };
    // Scale can't coarsen a leaf the camera is inside (distance 0 stays
    // under every band). With big overlapping leaf boxes (far floaters
    // stretch them — Cinevilla Forest Village) dozens of leaves sit at
    // distance 0, so even scale 64 planned 3.8M against a phone's 1.3M; the
    // pool filled with level 0 and the refines that didn't fit failed and
    // re-decoded every few seconds until iOS killed the page (2026-10-27).
    // Then plan per leaf instead: every visible leaf starts at its coarsest
    // level, and finer levels go out nearest first — by how close the tile's
    // splats are (see NEAR_SAMPLES; box centre until a chunk has landed — a
    // floater-stretched box is at distance 0 too), one level at a time:
    // level l at centre distance d ranks as d / PLAN_STEP^l. PLAN_STEP is
    // well under the bands' own multiplier, so what's in front of the camera
    // reaches full detail before tiles a few times farther get their next
    // level. So the budget fills the view from the camera outwards and the
    // background stays coarse when it runs short. (A single
    // level floor for the whole view first spent ~0.7M of an iPad's 3M on
    // background at level 2 while the house in front stayed soft, 2026-10-28.)
    this._levelFloor = 0;
    this._floorRelief.clear();
    this._planRecheckAt = 0;
    if (fits(64)) return search();
    floor = maxLevel;
    this._levelFloor = maxLevel;
    // Leaves just walked past only count for what the pool can't hold beside
    // the plan (at their coarsest level): right after turning back, their
    // detail took the whole plan and the view in front stayed coarse — and
    // with the camera at rest nothing re-planned once they aged out
    // (2026-10-28). An iPad's pool has room for them; a phone's may not.
    // Re-plan when they age out.
    const poolRoom = Math.max(0, this.pool.capacity * OFFSCREEN_HOLD_UNTIL - usable);
    let sum = total(64) - fixedSum + Math.max(0, fixedCoarse - poolRoom), draw = drawSum;
    if (Number.isFinite(fixedExpiry)) this._planRecheckAt = fixedExpiry + 50;
    const PLAN_STEP = 1.6;
    const centre = this._centreScratch || (this._centreScratch = camera.position.clone());
    const cur = [], steps = [];
    for (let k = 0; k < vn; k++) {
      const lods = visLods[k];
      let c = maxLevel;
      while (c >= 0 && !lods[c]) c--;
      cur[k] = c;
      if (c <= 0) continue;
      const cd = this._nearDistance(visIdx[k], camera.position) ?? visLeaf[k].box.getCenter(centre).distanceTo(camera.position);
      for (let l = c - 1; l >= 0; l--) if (lods[l]) steps.push([cd / Math.pow(PLAN_STEP, l), k, l]);
    }
    // Moving: tiles keep the finer level they already have (nearest first,
    // up to the full budget) — the detail lever only limits what's added.
    if (hold) {
      const kept = [];
      for (let k = 0; k < vn; k++) {
        const r = this.resident[visIdx[k]];
        if (r.level >= 0 && r.level < cur[k] && visLods[k][r.level]) kept.push(k);
      }
      kept.sort((a, b) => visDist[a] - visDist[b]);
      for (const k of kept) {
        const l = this.resident[visIdx[k]].level;
        const fine = visLods[k][l].count, coarse = visLods[k][cur[k]].count;
        const extra = this._bucketFn(fine) - this._bucketFn(coarse);
        const extraDraw = (fine - coarse) * visShare[k];
        if (sum + extra > hold.usable || draw + extraDraw > hold.draw) continue;
        sum += extra; draw += extraDraw;
        cur[k] = l;
      }
    }
    steps.sort((a, b) => a[0] - b[0]);
    const stopped = new Uint8Array(vn);
    for (const [, k, l] of steps) {
      if (stopped[k] || l >= cur[k]) continue;
      const fine = visLods[k][l].count, coarse = visLods[k][cur[k]].count;
      const extra = this._bucketFn(fine) - this._bucketFn(coarse);
      const extraDraw = (fine - coarse) * visShare[k];
      if (sum + extra > usable || draw + extraDraw > drawUsable) { stopped[k] = 1; continue; }
      sum += extra; draw += extraDraw;
      cur[k] = l;
    }
    for (let k = 0; k < vn; k++) if (cur[k] >= 0 && cur[k] < maxLevel) this._floorRelief.set(visLeaf[k], cur[k]);
    // The plan sets each visible leaf's level (_desiredLevel clamps to it);
    // the finest scale keeps the distance bands from overriding it.
    return minScale;
  }

  // Distance from `p` to the NEAR_PCT-th nearest sampled splat of leaf
  // `leafIndex`, or null before any of its chunks has landed — see NEAR_SAMPLES.
  _nearDistance(leafIndex, p) {
    const s = this._nearSamples[leafIndex];
    if (!s) return null;
    const n = s.length / 3, d = this._nearDistScratch.subarray(0, n);
    for (let j = 0; j < n; j++) {
      const dx = s[j * 3] - p.x, dy = s[j * 3 + 1] - p.y, dz = s[j * 3 + 2] - p.z;
      d[j] = dx * dx + dy * dy + dz * dz;
    }
    d.sort();
    return Math.sqrt(d[Math.floor((n - 1) * NEAR_PCT)]);
  }

  // NEAR_SAMPLES centres spread evenly through a just-written chunk, mapped
  // to render space like toRenderSpaceBox (sortXYZI holds x, y, -z of the
  // source; the drawn position is x, -y, -z).
  _takeNearSamples(leafIndex, xyzi, count) {
    const n = Math.min(NEAR_SAMPLES, count);
    if (!n) return;
    const s = new Float32Array(n * 3);
    for (let j = 0; j < n; j++) {
      const o = Math.floor((j + 0.5) * count / n) * 4;
      s[j * 3] = xyzi[o]; s[j * 3 + 1] = -xyzi[o + 1]; s[j * 3 + 2] = xyzi[o + 2];
    }
    this._nearSamples[leafIndex] = s;
  }

  // Share (0–1) of a box's projected outline inside the view: its 8 corners
  // through the view-projection, the NDC bounding rectangle clipped to
  // [-1, 1]². 1 if any corner is behind the camera (it may fill the view).
  _onScreenShare(box) {
    const m = this._projScreenMatrix.elements;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let c = 0; c < 8; c++) {
      const x = c & 1 ? box.max.x : box.min.x;
      const y = c & 2 ? box.max.y : box.min.y;
      const z = c & 4 ? box.max.z : box.min.z;
      const w = m[3] * x + m[7] * y + m[11] * z + m[15];
      if (w <= 1e-6) return 1;
      const nx = (m[0] * x + m[4] * y + m[8] * z + m[12]) / w;
      const ny = (m[1] * x + m[5] * y + m[9] * z + m[13]) / w;
      if (nx < minX) minX = nx; if (nx > maxX) maxX = nx;
      if (ny < minY) minY = ny; if (ny > maxY) maxY = ny;
    }
    const area = (maxX - minX) * (maxY - minY);
    if (!(area > 1e-9)) return 1;
    const ix = Math.max(0, Math.min(1, maxX) - Math.max(-1, minX));
    const iy = Math.max(0, Math.min(1, maxY) - Math.max(-1, minY));
    return Math.min(1, (ix * iy) / area);
  }

  // Projected-screen-size proxy for a leaf's "value" — used only by
  // _solveKnapsack (?knapsack=1) to rank candidate upgrades/demotes against
  // each other. This data format (lod-meta.json) carries no precomputed
  // per-level error/coverage metric the way PlayCanvas's apparently does
  // (see gsplat-budget-balancer.js's "coverage x error-reduction / cost"),
  // so radius^2/distance^2 (bigger/closer leaves matter more) is the closest
  // cheap substitute available from this frontend alone — a real, disclosed
  // fidelity gap versus PlayCanvas, not one this file can close on its own.
  _leafCoverage(distance, radius) {
    const d = Math.max(1, distance);
    return (radius * radius) / (d * d);
  }

  // ?knapsack=1: per-leaf value-ranked greedy allocator for VISIBLE leaves,
  // replacing _solveBudgetScale's single global distance-scale search.
  // Deliberately a NARROWER change than a full PlayCanvas-style port — see
  // the implementation plan's Part A for the full reasoning — scoped to
  // just answering "what level does each visible, already-resident leaf
  // want" via a Map the caller (_applyDesiredLevels) consults instead of
  // calling _desiredLevel(camera, leaf, scale, res.level) inline. Every
  // other piece of _applyDesiredLevels (off-screen cull/free/demote,
  // prewarm sweep, predictive prefetch, request issuance/capping, first-fill
  // backoff) is completely untouched and still runs exactly as before —
  // this only decides the "visible AND already has something resident"
  // case; a never-resident visible leaf still goes straight to coarsest via
  // _applyDesiredLevels' existing `neverShown` branch (checked before this
  // map is consulted), matching today's fast-first-fill behaviour.
  //
  // Deliberate, explicit deviation from PlayCanvas: rather than resetting
  // every leaf to its cheapest level and re-buying from scratch each solve
  // (cheap for them — their GPU work-buffer rebuild is near-zero marginal
  // cost), this treats each leaf's CURRENTLY resident level as a sunk-cost
  // baseline and only ever evaluates the single next step up or down from
  // there. A reset-and-rebuy model here would mean redundant free+realloc+
  // redecode churn on every solve — exactly what LOD_HYSTERESIS exists to
  // eliminate for the legacy solver too.
  _solveKnapsack(camera, usable) {
    const now = performance.now();
    const fovScale = this._fovScale || 1;
    const leaves = this.meta.leaves;
    const bucketFn = this._bucketFn;
    let residentCost = 0;
    const upgradeCands = []; // { leafIndex, level, entry, costDelta, value }
    const demoteCands = []; // costDelta here is the SAVINGS if demoted (positive)

    for (let i = 0; i < leaves.length; i++) {
      const leaf = leaves[i];
      const res = this.resident[i];
      const visible = this._frustum.intersectsBox(leaf.box);
      if (!visible) {
        // Off-screen-but-fresh resident leaves count against the budget at
        // their current size, same accounting _solveBudgetScale's fixedSum
        // already does — this solver doesn't touch their level at all
        // (that's _applyDesiredLevels' cull state machine's job).
        if (res.level >= 0 && now - (res.lastVisibleAt || 0) <= CULL_FREE_MS && leaf.lods[res.level]) {
          residentCost += bucketFn(leaf.lods[res.level].count);
        }
        continue;
      }
      if (res.level < 0) continue; // never-resident visible leaf — handled by the neverShown->coarsest fast path outside this solver
      const curEntry = leaf.lods[res.level];
      const curCost = curEntry ? bucketFn(curEntry.count) : 0;
      residentCost += curCost;

      const distance = leaf.box.distanceToPoint(camera.position) * fovScale;
      const target = this._desiredLevel(camera, leaf, 1, res.level); // scale=1: no global scale under knapsack; hysteresis alone gates eligibility
      if (target === res.level) continue;

      if (target < res.level) {
        // Wants finer — evaluate only the single next-finer step.
        const nextLevel = res.level - 1;
        const entry = leaf.lods[nextLevel];
        if (!entry) continue;
        const costDelta = bucketFn(entry.count) - curCost;
        upgradeCands.push({ leafIndex: i, level: nextLevel, entry, costDelta, value: this._leafCoverage(distance, leaf.radius) });
      } else {
        // Wants coarser — evaluate only the single next-coarser step.
        const nextLevel = res.level + 1;
        const entry = leaf.lods[nextLevel];
        if (!entry) continue;
        const costDelta = curCost - bucketFn(entry.count); // positive = splats freed by demoting
        demoteCands.push({ leafIndex: i, level: nextLevel, entry, costDelta, value: this._leafCoverage(distance, leaf.radius) });
      }
    }

    const desiredMap = new Map();

    // Demote pass — only runs under genuine budget pressure. Least-valuable
    // (smallest/farthest) leaves give back their room first.
    if (residentCost > usable && demoteCands.length) {
      demoteCands.sort((a, b) => a.value - b.value);
      for (const c of demoteCands) {
        if (residentCost <= usable) break;
        desiredMap.set(c.leafIndex, c.level);
        residentCost -= c.costDelta;
      }
    }

    // Upgrade pass — spends whatever headroom remains. Free/negative-cost
    // upgrades are always taken; the rest are ranked by value-per-splat and
    // bought best-first. Deliberately SKIPS a candidate that doesn't fit
    // instead of stopping the pass there (PlayCanvas's own rule, but that
    // rule assumes their reset-and-rebuy-every-solve model, where a blocked
    // candidate just gets re-ranked fresh next solve; this solver only
    // evaluates one step per leaf per solve — section 20's deliberate
    // deviation — so a single expensive, high-ranked candidate can occupy
    // the front of this same sorted list solve after solve and permanently
    // starve every cheaper candidate behind it. Observed live: leaves stuck
    // at a low LOD indefinitely right in front of the camera, with an idle
    // queue (nothing even requested) — see issue6.jpeg.
    let headroom = usable - residentCost;
    if (headroom > 0 && upgradeCands.length) {
      const free = [];
      const paid = [];
      for (const c of upgradeCands) {
        if (desiredMap.has(c.leafIndex)) continue; // already decided by the demote pass above (can't happen — disjoint by construction — but stay defensive)
        (c.costDelta <= 0 ? free : paid).push(c);
      }
      for (const c of free) {
        desiredMap.set(c.leafIndex, c.level);
        headroom -= c.costDelta;
      }
      paid.sort((a, b) => (b.value / b.costDelta) - (a.value / a.costDelta));
      for (const c of paid) {
        if (c.costDelta > headroom) continue;
        desiredMap.set(c.leafIndex, c.level);
        headroom -= c.costDelta;
      }
    }

    // A leaf in upgradeCands/demoteCands wants a level other than res.level —
    // true regardless of whether it made it into desiredMap this solve (it
    // might already be mid-flight toward the single next step, deduped by
    // _applyDesiredLevels' `desired === res.pendingLevel` check). Multi-level
    // moves only ever advance one step per solve (this function's own
    // deliberate deviation from PlayCanvas — see the class comment above),
    // so a leaf several levels off its target needs several MORE solves
    // after this one's request lands. update()'s `busy` gate only re-solves
    // on camera movement or genuinely queued/pending work — once this
    // solve's in-flight decode completes with the camera stationary and
    // nothing else outstanding, `busy` goes false and update() stops calling
    // this function at all, silently freezing every such leaf part-way to
    // its target. This flag is _applyDesiredLevels' signal (via
    // `this._wantsMoreDetail`) to keep the loop alive across that gap.
    // Verified against issue7.jpeg: idle queue (nothing pending) with
    // visible content still mostly coarse (L0:14 of 926 visible leaves) —
    // upgrades were being generated but the loop stopped advancing them
    // between camera moves.
    this._knapsackHasPendingWork = upgradeCands.length > 0 || demoteCands.length > 0;

    return desiredMap;
  }

  // The coarsest LOD entry a leaf actually has (lods can be sparse), as
  // { level, entry }, or null for a degenerate leaf with no levels at all.
  _coarseEntry(leaf) {
    for (let l = this._coarsest; l >= 0; l--) {
      if (leaf.lods[l]) return { level: l, entry: leaf.lods[l] };
    }
    return null;
  }

  _desiredLevel(camera, leaf, scale, currentLevel = -1) {
    // Distance to the NEAREST point on the leaf's box (0 inside it), NOT to
    // its center — see the constructor's baseDistance comment. Then scaled
    // by the camera's effective FOV so a zoomed-in view earns finer detail.
    const distance = leaf.box.distanceToPoint(camera.position) * (this._fovScale || 1);
    const maxLevel = this.meta.lodLevels - 1;
    let level = maxLevel;
    // Divide by scale, not multiply: _solveBudgetScale's binary search needs
    // total(scale) to DECREASE as scale grows (that's the whole lever it
    // pulls to fit the budget — see its own comment, "inflates all
    // distances, pushing nodes coarser"). Shrinking the threshold is what
    // makes a given distance harder to qualify as "close enough" for a fine
    // level as scale increases. Multiplying instead (the bug this replaces)
    // did the opposite — grew the threshold, made MORE leaves qualify for
    // finer levels as scale increased — so the search chased a `scale` that
    // pushed total further past budget, not toward it, converging on a huge
    // scale that made nearly every leaf want its finest level at once. Most
    // of those pool.alloc() calls then failed (correctly — the pool was
    // never going to hold that much), and any leaf that had never
    // successfully landed data before stayed permanently empty: exactly the
    // persistent grey blocks, not a transient streaming gap.
    // Symmetric hysteresis around the resident level's band: the camera has
    // to move notably PAST band(currentLevel) to coarsen (×HYST) and notably
    // INSIDE band(currentLevel-1) to refine (÷HYST). Without the refine side,
    // a chunk sitting right at a band edge during smooth motion flips level
    // every frame — each flip swaps in a different decimated splat subset, so
    // fine detail (a sign, marks on glass) visibly pops/flickers.
    for (let l = 0; l <= maxLevel; l++) {
      let threshold = (this.baseDistance * Math.pow(this.lodMultiplier, l)) / scale;
      if (currentLevel >= 0) {
        if (l === currentLevel) threshold *= LOD_HYSTERESIS;
        else if (l === currentLevel - 1) threshold /= LOD_HYSTERESIS;
      }
      if (distance < threshold) { level = l; break; }
    }
    const floor = this._floorRelief.get(leaf) ?? this._levelFloor;
    if (level < floor) level = floor;
    // Fall back to the nearest coarser (then finer) level actually present.
    if (!leaf.lods[level]) {
      let found = -1;
      for (let l = level; l <= maxLevel; l++) if (leaf.lods[l]) { found = l; break; }
      if (found === -1) for (let l = level - 1; l >= 0; l--) if (leaf.lods[l]) { found = l; break; }
      level = found;
    }
    return level;
  }

  // `desiredMap`, when passed (only from the ?knapsack=1 path — see
  // update()), overrides how a VISIBLE, already-resident leaf's target level
  // is computed: instead of calling _desiredLevel(camera, leaf, scale,
  // res.level) inline below, a leaf present in the map uses that decision;
  // one absent from the map means "no change this solve" (falls back to
  // res.level, i.e. steady state) rather than "not visible" — _solveKnapsack
  // only ever adds an entry when it actually decided to move a leaf.
  // `atRest` (camera still past IDLE_REFINE_MS): a visible leaf may refine
  // straight to its planned level, skipping the refine side of the
  // hysteresis. That side exists to stop flips during motion; at rest it
  // left leaves one level under the plan forever — on Forest Village at 8M,
  // 72 nearby leaves sat at level 1 beside level-0 neighbours (sharp and
  // soft blocks that never cleared, 2026-10-27). The plan already fits the
  // budget; the coarsen side still holds them once the camera moves again.
  _applyDesiredLevels(camera, scale, desiredMap = null, atRest = false) {
    const byFile = new Map(); // fileIndex -> [{leafIndex, level, entry}]
    let visibleLeaves = 0;
    const now = performance.now();
    // Set when a level change was skipped this pass only because the camera
    // is moving fast — keeps update()'s solve loop alive (via `busy`) after
    // the camera stops so the deferred change actually happens then.
    this._deferredUpgrades = false;
    // Cap off-screen demote work per solve so a pan that ages many chunks
    // past CULL_DEMOTE_MS at once doesn't fire a burst of decode+upload for
    // things that aren't even visible (which would itself hitch). The rest
    // demote on the next solves.
    const boosting = this._boosting();
    let demoteBudget = boosting ? 30 : 3;
    // Room left before the in-flight + queued ceiling; how many requests we
    // may issue this solve. Candidates are gathered first, then sorted by
    // distance so these slots always go to the NEAREST leaves that need work
    // — a grey block right in front of the camera never waits behind a
    // distant one.
    const queueRoom = Math.max(0, this._maxWriteQueue - this._writeQueue.length - this._pendingDecodes);
    const requestCap = Math.min(queueRoom, boosting ? this._boostRequestCap : MAX_REQUESTS_PER_SOLVE);
    const candidates = []; // { leafIndex, desired, entry, dist }

    // Prefetch is active only when actually moving (>~2 units/s) and there's
    // request headroom. Its candidates carry PREFETCH_DIST_BIAS in their
    // sort key so they're always served after real on-screen work.
    const coarsest = this.meta.lodLevels - 1;
    const doPrefetch = queueRoom > 0 && this._camVel.lengthSq() > 4;
    const prefetchPoint = this._prefetchPoint;
    const prefetchRadius = this.baseDistance * 2;

    let visibleEmpty = 0;
    // How much of the CURRENT VIEW is loaded at the level the solver wants
    // (splat-weighted) — drives the streamed loading ring and "initial view
    // fully loaded" (see viewStatus()). Visible leaves only; prewarm and
    // off-screen work don't count.
    const viewTargets = []; // [leafIndex, wanted level] for visible leaves
    this._viewStale = false;
    if (this._failRetryAt > 0 && now >= this._failRetryAt) this._failRetryAt = 0;
    const holdOffscreen = this.pool.residentCount < this.pool.capacity * OFFSCREEN_HOLD_UNTIL;
    this.meta.leaves.forEach((leaf, leafIndex) => {
      const visible = this._frustum.intersectsBox(leaf.box);
      if (visible) visibleLeaves++;
      if (visible && this.resident[leafIndex].level === -1) visibleEmpty++;
      const res = this.resident[leafIndex];
      if (visible) { res.lastVisibleAt = now; res.everVisible = true; }
      // A leaf that has never had anything resident (just entered view —
      // panned/zoomed into a new area, or this is the very first solve)
      // has no fallback while its real target level decodes: it shows
      // nothing at all until that lands, which is exactly the "grey block
      // that moves with the camera" symptom. Ask for the cheap coarsest
      // level FIRST in that case regardless of true distance — it decodes
      // fast (smallest file), gives something to show immediately, and the
      // next solve (this already re-runs continuously) naturally requests
      // the real target level once res.level is no longer -1.
      // Cull cooldown: don't free a chunk the instant it leaves the
      // frustum. During ordinary navigation, chunks at the screen edge
      // flip in and out of the frustum constantly as the camera rotates —
      // freeing each one immediately means it has to re-decode (async) and
      // shows a gap until it lands, then refines back up. That
      // free/re-decode churn at the screen edges IS the "blocks flickering
      // on navigation". Keep a chunk resident (at whatever level it has)
      // for a grace period after it was last visible; only genuinely free
      // it once it's been gone a while.
      const staleFor = now - (res.lastVisibleAt || 0);
      // Both gated on res.everVisible: a leaf prewarm filled but the camera
      // has never actually looked at yet has an "infinite" staleFor (no
      // lastVisibleAt to measure from) and must NOT be caught by either
      // check — that was the bug in the first version of this (see the
      // constructor's comment): it demoted (a no-op, already coarsest) then
      // FREED almost immediately after prewarm filled it in, right back to
      // grey, every single leaf that was never actually visible. Only a
      // leaf that was genuinely on screen and has since been abandoned
      // should age out — that's the ONLY case staleFor is meaningful for.
      const freeNow = !holdOffscreen && !visible && res.everVisible && staleFor > CULL_FREE_MS;
      // Off-screen a while but not gone for good: hold it, but only at the
      // coarsest level — cheap insurance against a black hole when the
      // camera pans back. If it's already coarse, this is a no-op below.
      const demoteNow = !holdOffscreen && !visible && res.everVisible && !freeNow && staleFor > CULL_DEMOTE_MS
        && res.level >= 0 && res.level < coarsest && res.pendingLevel === -1 && demoteBudget > 0;
      if (demoteNow) { demoteBudget--; this._demoteCount = (this._demoteCount || 0) + 1; }

      const neverShown = res.level === -1 && res.pendingLevel === -1;
      const desired = freeNow ? -1
        : demoteNow ? coarsest
        : !visible ? res.level // still fresh off-screen — leave exactly as-is
        : neverShown ? coarsest
        : desiredMap ? (desiredMap.has(leafIndex) ? desiredMap.get(leafIndex) : res.level)
        : atRest ? Math.min(this._desiredLevel(camera, leaf, scale, res.level), this._desiredLevel(camera, leaf, scale, -1))
        : this._desiredLevel(camera, leaf, scale, res.level);

      if (visible) {
        // Target for a never-shown leaf is its real level, not the coarsest
        // stand-in requested first.
        const target = neverShown
          ? (desiredMap ? (desiredMap.has(leafIndex) ? desiredMap.get(leafIndex) : coarsest) : this._desiredLevel(camera, leaf, scale, -1))
          : desired;
        viewTargets.push([leafIndex, target]);
      }

      if (desired === -1) {
        // Genuinely gone — free it, and cancel any in-flight request FOR
        // THIS RESIDENT DATA (it's for a level we no longer want at all).
        // Both live inside the res.offset >= 0 guard now — see below for
        // why that matters.
        //
        // Found 2026-09-15 chasing "prewarm requests the sky but it never
        // actually loads, forever": desired hits -1 here NOT ONLY for a
        // leaf actually being freed, but also, incidentally, for any
        // never-visible leaf with nothing resident YET (`!visible ? res.level`
        // above evaluates to res.level's default of -1) — including one
        // with a perfectly legitimate prewarm/prefetch request already IN
        // FLIGHT (res.pendingLevel >= 0, res.offset still -1 because the
        // write hasn't landed). The old code reset res.pendingLevel = -1
        // UNCONDITIONALLY here regardless, silently cancelling that
        // tracking — so when the decode actually completed,
        // _fulfillRequests' `res.pendingLevel !== level` check now
        // mismatched and threw the finished result away. Every solve that
        // runs before a request resolves repeats this, so anything that
        // takes more than one solve to decode — near-guaranteed for
        // prewarm, working through potentially thousands of leaves via
        // only decodeWorkerCount=2 workers — loses the race and gets
        // discarded on completion, every single time: requested, decoded,
        // thrown away, forever. This most likely wasn't new — the existing
        // predictive-prefetch mechanism hits the exact same path, just
        // with few enough in-flight leaves, decoded fast enough, that it
        // usually won the race in practice.
        if (res.offset >= 0) {
          this.pool.free(res.offset, res.count);
          this.sortWorker.postMessage({ method: 'clear', offset: res.offset, count: res.count });
          res.offset = -1; res.count = 0; res.level = -1;
          this._freeCount = (this._freeCount || 0) + 1;
          res.pendingLevel = -1;
        }
        return;
      }
      // Fresh-off-screen chunks are left untouched — except a demote request
      // (allowed through as a candidate below), or a predictive prefetch:
      // nothing resident, camera heading this way, leaf near where we'll be
      // in PREFETCH_SECONDS → request its coarsest level now at low priority.
      if (!visible && !demoteNow) {
        if (doPrefetch && neverShown && now - res.allocFailAt >= ALLOC_FAIL_BACKOFF_MS) {
          const d2p = leaf.box.distanceToPoint(prefetchPoint);
          if (d2p < prefetchRadius) {
            const pe = leaf.lods[coarsest];
            if (pe) candidates.push({ leafIndex, desired: coarsest, entry: pe, dist: d2p + PREFETCH_DIST_BIAS });
          }
        }
        return;
      }
      // Already showing this level, or already fetching it — nothing to do.
      // The pendingLevel check matters: without it, repeated solves (every
      // small camera move re-runs this) would re-queue the same in-flight
      // leaf/level on every pass until the first request finally resolves,
      // each one landing its own separate pool.alloc() for the same data.
      // Hold visible leaves at whatever level they already have while the
      // camera is really moving — don't chase finer detail you'd fly past in
      // a frame. Each level change is a decode+alloc+write+move cycle, and a
      // burst of them mid-pan is the per-object shimmer. First-fill for a
      // blank leaf and off-screen demotes still go through; a visible
      // coarsen goes through only under real budget pressure (scale < 1),
      // not just because the chunk receded. Everything catches up the
      // moment you slow down. (Lower level number = finer.)
      const fastMotion = this._camVel.lengthSq() > ((this.baseDistance || 50) * 0.1) ** 2;
      if (res.level >= 0 && desired >= 0 && fastMotion
          && (desired < res.level || (visible && desired > res.level && scale >= 1))) {
        this._deferredUpgrades = true;
        return;
      }

      if (desired === res.level || desired === res.pendingLevel) return;
      // First-fill backoff — see ALLOC_FAIL_BACKOFF_MS. Only gates a leaf
      // with NOTHING resident yet; refining an already-resident leaf isn't
      // subject to this (smaller, less frequent failure mode).
      if (res.level === -1 && now - res.allocFailAt < ALLOC_FAIL_BACKOFF_MS) return;
      if (res.level >= 0 && desired === res.failLevel && now - res.failAt < REFINE_FAIL_RETRY_MS) return;

      const entry = leaf.lods[desired];
      if (!entry) return;
      // Distance used only for prioritisation here (raw, not FOV-scaled) —
      // demotes are off-screen so give them a large sort key so on-screen
      // work always wins.
      const dist = demoteNow ? Infinity : leaf.box.distanceToPoint(camera.position);
      // Tier 0 = visible with nothing resident (coverage) — always first.
      candidates.push({ leafIndex, desired, entry, dist, coverage: visible && res.level === -1 });
    });
    this._visibleEmpty = visibleEmpty;
    this._viewTargets = viewTargets;

    // ─── Base-layer prewarm ────────────────────────────────────────────────
    // One-time sweep after load: request the coarsest level for EVERY leaf
    // that still has nothing resident and nothing in flight — including
    // leaves the camera has never faced — so the whole scene fills in blurry
    // colour rather than only the part that's been on screen. Candidates
    // carry PREFETCH_DIST_BIAS so real on-screen work is always served
    // first; requestCap + MAX_WRITE_QUEUE keep it from spiking a frame, and
    // `_prewarmPending` (also part of update()'s `busy`) keeps solves
    // running until the sweep is done.
    if (this._prewarmPending) {
      const budgetCap = this.budget * PREWARM_BUDGET_FRACTION;
      if (this._prewarmBudgetUsed >= budgetCap) {
        // Capped out — see the constructor's comment. Whatever's left
        // un-prewarmed (typically the farthest things) just stays grey
        // until the camera actually reaches it, same as before prewarm
        // existed. Stop the per-solve full-leaf scan permanently rather
        // than repeating it forever for no effect.
        this._prewarmPending = false;
      } else {
        const queued = new Set(candidates.map((c) => c.leafIndex));
        let stillPending = false;
        for (let leafIndex = 0; leafIndex < this.meta.leaves.length; leafIndex++) {
          const res = this.resident[leafIndex];
          if (res.level >= 0 || res.pendingLevel >= 0) continue;
          stillPending = true;
          if (queued.has(leafIndex)) continue; // already a candidate this solve
          if (now - res.allocFailAt < ALLOC_FAIL_BACKOFF_MS) continue; // first-fill backoff
          const ce = this._coarseEntry(this.meta.leaves[leafIndex]);
          if (!ce) continue;
          const dist = this.meta.leaves[leafIndex].box.distanceToPoint(camera.position);
          candidates.push({ leafIndex, desired: ce.level, entry: ce.entry, dist: dist + PREFETCH_DIST_BIAS });
        }
        this._prewarmPending = stillPending;
      }
    }

    // Nearest first within each group, then issue up to the cap — but
    // reserve PREWARM_MIN_PER_SOLVE slots for prewarm candidates specifically
    // (see its own comment) rather than one merged distance sort, which
    // would let sustained real demand starve prewarm's farthest — typically
    // the sky — indefinitely. Array.sort is stable, so filtering the two
    // groups apart afterward keeps each one's own nearest-first order.
    candidates.sort((a, b) => a.dist - b.dist);
    const issueOne = ({ leafIndex, desired, entry }) => {
      this.resident[leafIndex].pendingLevel = desired;
      this._pendingDecodes++;
      this._decodeReqCount = (this._decodeReqCount || 0) + 1;
      let list = byFile.get(entry.file);
      if (!list) { list = []; byFile.set(entry.file, list); }
      list.push({ leafIndex, level: desired, entry });
    };
    const coverageCandidates = [];
    const realCandidates = [];
    const prewarmCandidates = [];
    for (const c of candidates) {
      if (c.coverage) coverageCandidates.push(c);
      else (c.dist >= PREFETCH_DIST_BIAS ? prewarmCandidates : realCandidates).push(c);
    }
    // Coverage first, nearest first, limited only by the in-flight ceiling
    // (queueRoom) — not by MAX_REQUESTS_PER_SOLVE.
    let coverageIssued = 0;
    for (let i = 0; i < coverageCandidates.length && coverageIssued < queueRoom; i++, coverageIssued++) issueOne(coverageCandidates[i]);
    const prewarmMin = prewarmCandidates.length ? Math.min(PREWARM_MIN_PER_SOLVE, requestCap) : 0;
    const realCap = Math.max(0, requestCap - prewarmMin);
    let issued = 0;
    for (let i = 0; i < realCandidates.length && issued < realCap; i++, issued++) issueOne(realCandidates[i]);
    for (let i = 0; i < prewarmCandidates.length && issued < requestCap; i++, issued++) {
      issueOne(prewarmCandidates[i]);
      // Tracked at issuance, not at confirmed pool.alloc() success — a
      // request that later fails to allocate didn't really consume pool
      // space, so this slightly overcounts, but that only makes the cap
      // (PREWARM_BUDGET_FRACTION) a little more conservative, never less —
      // the safe direction. See the constructor's comment for why this
      // needs to be a real cap at all.
      this._prewarmBudgetUsed += this._bucketFn(prewarmCandidates[i].entry.count);
    }
    // Anything still wanting a change (more candidates than we could issue,
    // or none but decodes still landing) means keep solving next frame even
    // if the camera is still — see update()'s `busy` check. Under knapsack
    // (desiredMap passed), also OR in _knapsackHasPendingWork: a leaf can be
    // mid-flight toward an intermediate single step (so absent from
    // `candidates` this pass — see _solveKnapsack's comment) while still
    // several steps short of its real target.
    this._wantsMoreDetail = candidates.length > 0 || (desiredMap != null && this._knapsackHasPendingWork);

    for (const [fileIndex, requests] of byFile) {
      this.decodePool.request(fileIndex, this.meta.filenames[fileIndex])
        .then((decoded) => this._fulfillRequests(decoded, requests))
        .catch((err) => {
          console.error('[SplatOctree] decode failed', fileIndex, err);
          // Let a future solve retry — otherwise a permanently-stuck
          // pendingLevel would block this leaf from ever being requested
          // again, even once its desired level changes back to something
          // reachable.
          for (const { leafIndex, level } of requests) {
            this._pendingDecodes = Math.max(0, this._pendingDecodes - 1);
            const res = this.resident[leafIndex];
            if (res.pendingLevel === level) res.pendingLevel = -1;
          }
        });
    }

    // Trim the decode cache: keep files backing a resident or in-flight leaf,
    // drop the rest down to MAX_DECODE_CACHE.
    const keep = new Set();
    for (const r of this.resident) {
      if (r.level >= 0 && r.fileIndex >= 0) keep.add(r.fileIndex);
    }
    for (const item of this._writeQueue) keep.add(item.entry.file);
    this.decodePool.evict(keep, this._maxDecodeCache);

    window.__splatOctreeStats = {
      ...window.__splatOctreeStats,
      visibleLeaves,
      budget: this.budget,
      freesTotal: this._freeCount || 0,
      demotesTotal: this._demoteCount || 0,
      decodeRequestsTotal: this._decodeReqCount || 0,
      decodeCacheSize: this.decodePool._cache.size,
    };
    this._publishStats();
  }

  _publishStats() {
    const residentSplats = this.resident.reduce((s, r) => s + r.count, 0);
    const residentLeaves = this.resident.filter((r) => r.level >= 0).length;
    // Per-level breakdown — how many resident leaves are actually sitting
    // at each LOD level right now, not just the total. "Is level 0 ever
    // reached at all" is a very different question from "how many splats
    // total", and the plain total can't answer it.
    const leavesByLevel = {};
    for (const r of this.resident) if (r.level >= 0) leavesByLevel[r.level] = (leavesByLevel[r.level] || 0) + 1;
    const prewarmLeft = this._prewarmPending
      ? this.resident.reduce((n, r) => n + (r.level < 0 && r.pendingLevel < 0 ? 1 : 0), 0)
      : 0;
    // Stopped early because PREWARM_BUDGET_FRACTION was reached, not
    // because every leaf actually got prewarmed — prewarmLeft would read 0
    // either way, so this distinguishes "done" from "gave up with leaves
    // still grey" for the debug overlay.
    const prewarmCapped = this._prewarm && !this._prewarmPending
      && this._prewarmBudgetUsed >= this.budget * PREWARM_BUDGET_FRACTION;
    window.__splatOctreeStats = {
      ...window.__splatOctreeStats,
      residentSplats, residentLeaves, leavesByLevel, budget: this.budget,
      writeQueueLen: this._writeQueue.length,
      pendingDecodes: this._pendingDecodes,
      decodeStalls: this.decodePool.stalls || 0, // workers restarted — see DECODE_STALL_MS
      fetch: this.decodePool.fetchStats(),
      allocFailures: this._allocFailures || 0,
      poolResident: this.pool.residentCount,
      poolUtilization: this.pool.capacity > 0 ? this.pool.residentCount / this.pool.capacity : 0,
      prewarmEnabled: this._prewarm, prewarmLeft, prewarmCapped,
      defragMoves: this._defragMoves || 0,
      defragQueueLen: this._defragQueue.length,
      writesTotal: this._writesTotal || 0, writtenSplatsTotal: this._writtenSplatsTotal || 0,
      detailPct: this.detailPct,
      evictsTotal: (this._evictsTotal || 0) + (this._freeCount || 0),
      ...this.pool.freeStats(),
    };
  }

  // Per-splat data for one [srcOff, srcOff+count) slice of an already-
  // decoded file, in the shape SplatPool.write() / the sort worker want.
  // Shared by normal fulfillment and by _compact()'s repack, which slices
  // the exact same way from the exact same (already-decoded, cached) file
  // data — a repack is never a re-decode, just re-placing existing bytes.
  _sliceChunk(decoded, srcOff, count) {
    return {
      centerAndScale: decoded.centerAndScale.subarray(srcOff * 4, (srcOff + count) * 4),
      covAndColor: decoded.covAndColor.subarray(srcOff * 4, (srcOff + count) * 4),
      sh: decoded.sh ? decoded.sh.subarray(srcOff * this.pool.shRowsPerSplat * 4, (srcOff + count) * this.pool.shRowsPerSplat * 4) : null,
    };
  }

  // A decode finished. Don't apply it here — enqueue it. Applying a chunk is
  // a synchronous GPU texture upload (SplatPool.write) on the main thread;
  // when several decodes land in the same tick (normal during navigation —
  // 4 workers, batched by file) doing them all at once is a 30-100ms frame
  // spike. Any spike lets accumulated camera input land in one step, which
  // can punch the camera through a wall for a frame or two — splats near the
  // near plane project to enormous streaks (the whole-screen "smear
  // flicker" every couple of seconds). _processWriteQueue drains a bounded
  // number per frame instead. pendingLevel stays set while queued so the
  // solver treats the chunk as in-flight and doesn't re-request it.
  _fulfillRequests(decoded, requests) {
    for (const { leafIndex, level, entry } of requests) {
      this._pendingDecodes = Math.max(0, this._pendingDecodes - 1);
      const res = this.resident[leafIndex];
      if (res.pendingLevel !== level) continue; // already superseded
      this._writeQueue.push({ decoded, leafIndex, level, entry });
    }
  }

  _processWriteQueue() {
    if (this._writeQueue.length === 0) return;
    if (typeof window !== 'undefined' && window.__perfNoStream) return; // ?perfNoStream=1 test
    // Time-boxed, not count-boxed: keep applying finished decodes until we've
    // spent WRITE_BUDGET_MS of this frame (more during a tuning boost), then
    // stop. A fixed count is either too slow on a weak device or too spiky
    // on a fast one; a wall-clock budget stays smooth on both. Always apply
    // at least one so the queue can't stall.
    const start = performance.now();
    const budgetMs = this._loading ? WRITE_BUDGET_MS_LOADING
      : this._boosting() || this._visibleEmpty > 0 ? WRITE_BUDGET_MS_BOOST : WRITE_BUDGET_MS;
    let applied = 0;
    this.pool.beginWrites();
    while (this._writeQueue.length > 0 && (applied === 0 || performance.now() - start < budgetMs)) {
      const item = this._writeQueue.shift();
      const { decoded, leafIndex, level, entry } = item;
      const res = this.resident[leafIndex];
      // Superseded while it sat in the queue — the camera moved and this
      // leaf now wants a different level (or nothing). pendingLevel is the
      // real signal (leaf.lods[level] is static and can't detect staleness).
      if (res.pendingLevel !== level) continue;
      res.pendingLevel = -1;
      applied++;
      this._viewStale = true; // re-solve so viewStatus() sees it — see _applyDesiredLevels

      const { offset: srcOff, count, file: fileIndex } = entry;
      let offset = this.pool.alloc(count);
      if (offset < 0 && res.level === -1) {
        // Coverage must not fail: evict off-screen leaves (oldest first) to
        // make room and retry; if that's still not enough, lower the whole
        // view's detail (budget pressure) so the solver frees room next.
        if (this._evictOffscreen(count)) offset = this.pool.alloc(count);
        if (offset < 0) {
          this._pressure = Math.max(PRESSURE_MIN, (this._pressure || 1) * PRESSURE_STEP_DOWN);
          this._lastCoverageFailAt = performance.now();
          this._prewarmPending = false; // don't keep refilling off-screen leaves under pressure
        }
      } else if (offset < 0 && this._frustum.intersectsBox(this.meta.leaves[leafIndex].box)) {
        // Refining something on screen: off-screen leaves give way too, or
        // after walking around the ones just passed held the pool and the
        // view in front never sharpened (2026-10-28).
        if (this._evictOffscreen(count)) offset = this.pool.alloc(count);
      }
      if (offset < 0) {
        // Buddy allocator coalesces freed blocks, so this is rare — it only
        // happens if the pool is genuinely near capacity. Keep the leaf at
        // whatever level it currently shows and move on. No repack, no
        // stall. The budget solver's USABLE_BUDGET_FRACTION headroom is what
        // keeps this from mattering.
        this._allocFailures = (this._allocFailures || 0) + 1;
        window.__splatOctreeStats = { ...window.__splatOctreeStats, allocFailures: this._allocFailures };
        console.warn('[SplatOctree] pool full, keeping previous LOD for leaf', leafIndex);
        if (res.level === -1) res.allocFailAt = performance.now(); // see ALLOC_FAIL_BACKOFF_MS
        // see REFINE_FAIL_RETRY_MS; failCount = consecutive failures at this level
        res.failCount = res.failLevel === level ? res.failCount + 1 : 1;
        // Less free space in the whole pool than this needs: not a transient
        // or fragmentation miss — a retry can't fit either, so it's at the
        // device limit now (viewStatus treats failCount >= 2 as limited).
        if (this.pool.freeStats().totalFree < count) res.failCount = Math.max(res.failCount, 2);
        res.failLevel = level; res.failAt = performance.now();
        // Wake the solver once the retry is due even if nothing else moves.
        if (!this._failRetryAt) this._failRetryAt = res.failAt + REFINE_FAIL_RETRY_MS + 50;
        // ?defrag=1 (exact-pack only): distinguish genuine exhaustion from
        // fragmentation the same way the poolFree debug-overlay row already
        // does — totalFree covering the request but no single interval doing
        // so means there's room, just not contiguous room. Queue a small,
        // bounded batch of already-resident leaves to slide; the retry that
        // eventually succeeds happens naturally via ALLOC_FAIL_BACKOFF_MS's
        // existing cadence, not a new one invented here.
        if (this._defragEnabled) {
          const fs = this.pool.freeStats();
          if (fs.totalFree >= count && fs.largestFreeBlock < count) this._queueDefragCandidates(DEFRAG_BATCH);
        }
        continue;
      }

      this.pool.write(offset, count, this._sliceChunk(decoded, srcOff, count));
      this._writesTotal = (this._writesTotal || 0) + 1; // [perf] line: chunk swaps
      this._writtenSplatsTotal = (this._writtenSplatsTotal || 0) + count;
      const sortSlice = decoded.sortXYZI.slice(srcOff * 4, (srcOff + count) * 4); // copy: transferred below
      if (!this._nearSamples[leafIndex]) this._takeNearSamples(leafIndex, sortSlice, count);

      // Free the pool bookkeeping for the old slot now (the replacement is
      // live), but hand the sort worker BOTH the new write and the old
      // clear in one 'move' message so no sort can land between them and
      // draw this chunk at both offsets (double alpha = a per-frame
      // brightness pulse while navigating). Freeing eagerly at decision
      // time — before the new data landed — opened the opposite gap
      // (nothing resident, grey patch); this keeps the old data live right
      // up until the new is in place.
      const oldOffset = res.offset >= 0 && res.offset !== offset ? res.offset : -1;
      const oldCount = res.count;
      if (oldOffset >= 0) this.pool.free(oldOffset, oldCount);
      this.sortWorker.postMessage(
        { method: 'move', offset, count, oldOffset, oldCount, data: sortSlice.buffer },
        [sortSlice.buffer]
      );
      res.level = level; res.offset = offset; res.count = count; res.fileIndex = fileIndex; res.srcOff = srcOff;
      if (res.failLevel === level) { res.failLevel = -1; res.failCount = 0; }
    }
    this.pool.endWrites();
    this._publishStats();
  }

  // Frees resident leaves that haven't been visible for a while, oldest
  // first, until at least `count` slots are free. Returns whether any were
  // freed. Part of the coverage guarantee (see PRESSURE_MIN).
  _evictOffscreen(count) {
    const now = performance.now();
    const victims = [];
    // Also test the frustum itself: lastVisibleAt is only refreshed while
    // solves run, so with the camera still (solver idle) every on-screen
    // leaf looked stale and one failed allocation evicted the whole view.
    this.resident.forEach((r, i) => {
      if (r.offset >= 0 && now - (r.lastVisibleAt || 0) > EVICT_OFFSCREEN_AFTER_MS
          && !this._frustum.intersectsBox(this.meta.leaves[i].box)) victims.push(i);
    });
    if (!victims.length) return false;
    victims.sort((a, b) => (this.resident[a].lastVisibleAt || 0) - (this.resident[b].lastVisibleAt || 0));
    let freed = false;
    for (const i of victims) {
      const r = this.resident[i];
      this.pool.free(r.offset, r.count);
      this.sortWorker.postMessage({ method: 'clear', offset: r.offset, count: r.count });
      this._evictsTotal = (this._evictsTotal || 0) + 1; // [perf] line
      r.offset = -1; r.count = 0; r.level = -1; r.pendingLevel = -1;
      this._freeCount = (this._freeCount || 0) + 1;
      freed = true;
      if (this.pool.freeStats().largestFreeBlock >= count) break;
    }
    return freed;
  }

  // Enqueue up to `n` already-resident leaves (that aren't already queued)
  // for a defrag slide. Simple, unweighted selection on purpose for this
  // first rollout (Stage 1) — which leaves specifically get slid isn't
  // load-bearing for correctness, only for how quickly fragmentation clears;
  // a smarter/proactive selection is a possible Stage 2 if reactive alone
  // proves insufficient on real devices. O(leaves) per call, but only ever
  // runs on a genuine fragmented-alloc-failure event, not every solve.
  _queueDefragCandidates(n) {
    let added = 0;
    for (let i = 0; i < this.resident.length && added < n; i++) {
      const res = this.resident[i];
      if (res.level < 0 || res.offset < 0 || this._defragQueued.has(i)) continue;
      this._defragQueued.add(i);
      this._defragQueue.push(i);
      added++;
    }
  }

  // Drains _defragQueue, time-boxed the same way _processWriteQueue is.
  // Strictly lower priority than real decode-driven writes (skips entirely
  // whenever _writeQueue is non-empty for this frame) — defrag is best-
  // effort housekeeping, never something that should compete with or delay
  // real LOD content landing. A "slide" reuses the EXACT same 'move'
  // sort-worker message and _sliceChunk() re-slicing path every normal LOD
  // swap already sends via _processWriteQueue — see this class's
  // constructor comment for why that specific reuse matters.
  _processDefragQueue() {
    if (!this._defragEnabled || this._defragQueue.length === 0 || this._writeQueue.length > 0) return;
    const start = performance.now();
    let moved = 0;
    this.pool.beginWrites();
    while (this._defragQueue.length > 0 && performance.now() - start < DEFRAG_BUDGET_MS) {
      const leafIndex = this._defragQueue.shift();
      this._defragQueued.delete(leafIndex);
      const res = this.resident[leafIndex];
      if (!res || res.level < 0 || res.offset < 0) continue; // no longer resident — freed/changed since queued
      // Dependency this relies on: the file backing any resident leaf is
      // guaranteed to still be in the decode cache (_applyDesiredLevels'
      // eviction trim exempts every file backing a resident leaf) — so this
      // is always a cheap re-slice, never a re-fetch/re-decode. If that
      // eviction guarantee is ever changed, this degrades silently rather
      // than failing loudly — worth remembering if defrag ever looks slower
      // than expected on device.
      const decoded = this.decodePool.getCached(res.fileIndex);
      if (!decoded) continue;
      const newOffset = this.pool.alloc(res.count);
      if (newOffset < 0 || newOffset === res.offset) continue; // no room gained, or nothing to gain
      this.pool.write(newOffset, res.count, this._sliceChunk(decoded, res.srcOff, res.count));
      const oldOffset = res.offset, oldCount = res.count;
      this.pool.free(oldOffset, oldCount);
      const sortSlice = decoded.sortXYZI.slice(res.srcOff * 4, (res.srcOff + res.count) * 4);
      this.sortWorker.postMessage(
        { method: 'move', offset: newOffset, count: res.count, oldOffset, oldCount, data: sortSlice.buffer },
        [sortSlice.buffer]
      );
      res.offset = newOffset;
      moved++;
    }
    this.pool.endWrites();
    if (moved > 0) {
      this._defragMoves = (this._defragMoves || 0) + moved;
      this._publishStats();
    }
  }

  destroy() {
    this.decodePool.destroy();
  }
}
